import React, { useState, useEffect, useRef } from "react";
import SidebarTopbar from "../layouts/layout";
import AbstractVideoViewDialog from "./AbstractVideoViewDialog";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Slider from "@mui/material/Slider";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Chip from "@mui/material/Chip";
import {
  RadioButtonUnchecked as CircleIcon,
  CheckCircle as CheckCircleIcon,
  ArrowBack as ArrowBackIcon,
  Autorenew as AutorenewIcon,
  PlayArrow as PlayArrowIcon,
  Pause as PauseIcon,
  VolumeUp as VolumeUpIcon,
  VolumeOff as VolumeOffIcon,
  Mic as MicIcon,
  Image as ImageIcon,
  Close as CloseIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  ClosedCaption as ClosedCaptionIcon,
  ClosedCaptionDisabled as ClosedCaptionDisabledIcon,
  ZoomIn as ZoomInIcon
} from "@mui/icons-material";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import IconButton from "@mui/material/IconButton";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import Tooltip from "@mui/material/Tooltip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import PropTypes from "prop-types";
import { useNotifications } from "../../contexts/NotificationContext";
import { useWebSockets } from "../../contexts/WebSocketContext";

const BASE_URL = process.env.REACT_APP_BASE_URL;
const PRIMARY_BLUE = "#1E4DE3";

const VideoEditorView = ({
  open,
  onClose,
  videoProjectId,
  token,
  publicationId,
  professionalType,
  onVideoCreated,
  isAlreadyMerged,
}) => {
  const [selectedSectionIndex, setSelectedSectionIndex] = useState(0);
  const [sections, setSections] = useState([]);
  const [sectionVersions, setSectionVersions] = useState({});
  const [selectedVersion, setSelectedVersion] = useState({});
  const [videoVersions, setVideoVersions] = useState({});
  const [selectedVideoVersion, setSelectedVideoVersion] = useState({});
  const [isChangingVideoVersion, setIsChangingVideoVersion] = useState(false);
  // Track regenerating content per section ID (Set of sequence_ids)
  const [regeneratingContentIds, setRegeneratingContentIds] = useState(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSections, setSelectedSections] = useState(new Set());
  const [isMerging, setIsMerging] = useState(false);
  const [mergeProgress, setMergeProgress] = useState(0);
  const [isMergeComplete, setIsMergeComplete] = useState(false);
  const [showApplyToAllDialog, setShowApplyToAllDialog] = useState(false);
  const [showMergedVideoDialog, setShowMergedVideoDialog] = useState(false);
  const mergeWebSocketRef = useRef(null);
  const [publicationTitle, setPublicationTitle] = useState("");
  const [activeTab, setActiveTab] = useState(0); // 0 = Content, 1 = Media
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [showCaptions, setShowCaptions] = useState(false); // Toggle captions visibility
  const [isMuted, setIsMuted] = useState(false); // Toggle mute state
  const [volume, setVolume] = useState(1); // 0 to 1
  const [isVolumeHovered, setIsVolumeHovered] = useState(false);
  // Track regenerated content per section ID (Mapping sequence_id -> content)
  const [regeneratedContentMap, setRegeneratedContentMap] = useState({});
  // Track applying status and progress per section ID
  const [applyingSectionIds, setApplyingSectionIds] = useState(new Set());
  const [applyProgressMap, setApplyProgressMap] = useState({});

  // Track image regeneration per section ID and slot
  const [regeneratingImageIds, setRegeneratingImageIds] = useState(new Set());
  const [regeneratingSlotIndices, setRegeneratingSlotIndices] = useState({}); // sequenceId -> slotIndex
  const [regeneratedImageDataMap, setRegeneratedImageDataMap] = useState({}); // Stores regenerated image versions before Apply per section
  const [selectedImageForApplyMap, setSelectedImageForApplyMap] = useState({}); // Tracks selected image version to apply per section
  const [applyingImageIds, setApplyingImageIds] = useState(new Set());
  const [applyingSlotInfo, setApplyingSlotInfo] = useState({});
  const [regenerationMessages, setRegenerationMessages] = useState({});
  const [uploadingSlotKeys, setUploadingSlotKeys] = useState(new Set());
  const [previewImage, setPreviewImage] = useState(null); // For image zoom preview modal
  const videoRef = useRef(null);
  const videoContainerRef = useRef(null);
  const imageUploadRef = useRef(null); // eslint-disable-line no-unused-vars
  const applyWebSocketRef = useRef(null);
  const { fetchNotifications, playNotificationSound } = useNotifications();
  const { taskStatus, activeTasks, setTaskStatus, setupWebSocket, setActiveTasks } = useWebSockets();

  // Alert state
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("success");

  // Confirmation dialog state
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmDialogAction, setConfirmDialogAction] = useState(null);

  // Show alert helper function
  const showAlert = (message, severity = "success") => {
    setAlertMessage(message);
    setAlertSeverity(severity);
    setAlertOpen(true);
  };

  const handleAlertClose = (event, reason) => {
    if (reason === "clickaway") return;
    setAlertOpen(false);
  };

  // Toggle section selection
  const toggleSectionSelection = (index, e) => {
    e.stopPropagation();
    setSelectedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  // Select all sections
  const handleSelectAll = () => {
    if (selectedSections.size === sections.length) {
      setSelectedSections(new Set());
    } else {
      setSelectedSections(new Set(sections.map((_, index) => index)));
    }
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Fetch video content when the view opens
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (open && videoProjectId && token) {
      fetchVideoContent();
    }
  }, [open, videoProjectId, token]);

  // When sections change, initialize version state and auto-select all
  useEffect(() => {
    if (sections.length > 0) {
      const newVersions = {};
      const newSelected = {};
      const newVideoVersions = {};
      const newSelectedVideoVersion = {};
      sections.forEach(section => {
        // Content versions
        const total = section.content_total_versions || 1;
        newVersions[section.sequence_id] = Array.from({ length: total }, (_, i) => i + 1);
        newSelected[section.sequence_id] = section.content_selected_version || section.content_version_number || total;

        // Video versions
        const videoTotal = section.video?.video_total_versions || 1;
        newVideoVersions[section.sequence_id] = Array.from({ length: videoTotal }, (_, i) => i + 1);
        newSelectedVideoVersion[section.sequence_id] = section.video?.video_selected_version || section.video?.video_version_number || videoTotal;
      });
      setSectionVersions(newVersions);
      setSelectedVersion(newSelected);
      setVideoVersions(newVideoVersions);
      setSelectedVideoVersion(newSelectedVideoVersion);
    }
  }, [sections]);

  // Reset video playback state when section or video URL changes
  useEffect(() => {
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
  }, [selectedSectionIndex, sections]);

  // Cleanup merge state when dialog closes
  useEffect(() => {
    if (!open) {
      setIsMerging(false);
      setMergeProgress(0);
      setIsMergeComplete(false);
      setShowMergedVideoDialog(false);
    }
  }, [open]);

  // Set initial state for already merged videos
  useEffect(() => {
    if (open) {
      if (isAlreadyMerged) {
        setIsMergeComplete(true);
      } else {
        setIsMergeComplete(false);
      }
    }
  }, [open, isAlreadyMerged]);

  // Prevent accidental navigation when generating/uploading images
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (uploadingSlotKeys.size > 0 || regeneratingImageIds.size > 0 || applyingImageIds.size > 0) {
        e.preventDefault();
        e.returnValue = "Changes you made may not be saved.";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [uploadingSlotKeys.size, regeneratingImageIds.size, applyingImageIds.size]);

  // Force reset when switching projects
  const prevVideoProjectId = useRef(videoProjectId);
  useEffect(() => {
    if (videoProjectId && videoProjectId !== prevVideoProjectId.current) {
      console.log("[VideoEditorView] Switched to new videoProjectId, resetting merge states");
      setIsMerging(false);
      setMergeProgress(0);
      setIsMergeComplete(false);
      setAlertOpen(false);
      prevVideoProjectId.current = videoProjectId;
    }
  }, [videoProjectId]);

  // Check for in-progress video merge/regenerate when dialog opens
  useEffect(() => {

    if (open && videoProjectId && publicationId) {
      // Check if there's an active video_merge or video_regenerate task from WebSocket
      const videoMergeTasks = Object.values(taskStatus.video_merge || {});
      const videoRegenerateTasks = Object.values(taskStatus.video_regenerate || {});

      const videoMergeStatus = videoMergeTasks.find(t => {
        if (!t) return false;
        const id = t.video_project_id || t.videoProjectId || t.projectId || t.videoId;
        return id && Number(id) === Number(videoProjectId) && Number(t.publicationId) === Number(publicationId);
      });

      const videoRegenerateStatus = videoRegenerateTasks.find(t => {
        if (!t) return false;
        const id = t.video_project_id || t.videoProjectId || t.projectId || t.videoId;
        return id && Number(id) === Number(videoProjectId) && Number(t.publicationId) === Number(publicationId);
      });

      console.log("[VideoEditorView] Checking for active video tasks:", {
        videoProjectId,
        publicationId,
        activeTasks,
        videoMergeStatus,
        videoRegenerateStatus
      });

      // Check for video_merge task (creating final video)
      if (videoMergeStatus && videoMergeStatus.progress > 0) {
        const publicationMatches = !videoMergeStatus.publicationId || String(videoMergeStatus.publicationId) === String(publicationId);
        const taskVideoId = videoMergeStatus.video_project_id || videoMergeStatus.videoProjectId || videoMergeStatus.projectId || videoMergeStatus.videoId;
        const projectMatches = taskVideoId && String(taskVideoId) === String(videoProjectId);

        // Restore video merge progress
        if (videoMergeStatus.progress < 100 && videoMergeStatus.status !== "completed" && videoMergeStatus.status !== "failed" && videoMergeStatus.status !== "error" && publicationMatches && projectMatches) {
          console.log("[VideoEditorView] Restoring in-progress video merge:", {
            progress: videoMergeStatus.progress,
            publicationId: videoMergeStatus.publicationId,
            status: videoMergeStatus.status
          });

          setIsMerging(true);
          setMergeProgress(videoMergeStatus.progress);
          setIsMergeComplete(false);
        }
        // Handle completed
        else if ((videoMergeStatus.progress >= 100 || videoMergeStatus.status === "completed") && publicationMatches && projectMatches) {
          console.log("[VideoEditorView] Restoring completed video merge");
          setMergeProgress(100);
          setIsMergeComplete(true);
          setIsMerging(false);
        }
      }

      // Check for video_regenerate task (regenerating content/images)
      if (videoRegenerateStatus && videoRegenerateStatus.progress > 0) {
        console.log("[VideoEditorView] Active video regenerate task detected:", {
          progress: videoRegenerateStatus.progress,
          publicationId: videoRegenerateStatus.publicationId
        });
      }
    }
  }, [open, videoProjectId, publicationId, activeTasks, taskStatus]);

  // Monitor WebSocket status for video_merge and video_regenerate tasks
  useEffect(() => {
    // Monitor video_merge progress
    const videoMergeTasks = Object.values(taskStatus.video_merge || {});
    const currentVideoMerge = videoMergeTasks.find(t => {
      if (!t) return false;
      const id = t.video_project_id || t.videoProjectId || t.projectId || t.videoId;
      return id && Number(id) === Number(videoProjectId) && Number(t.publicationId) === Number(publicationId);
    });
    
    if (currentVideoMerge && currentVideoMerge.progress > 0) {
      const progress = Number(currentVideoMerge.progress);
      const status = currentVideoMerge.status;
      const publicationMatches = !currentVideoMerge.publicationId || String(currentVideoMerge.publicationId) === String(publicationId);
      const taskVideoId = currentVideoMerge.video_project_id || currentVideoMerge.videoProjectId || currentVideoMerge.projectId || currentVideoMerge.videoId;
      const projectMatches = taskVideoId && String(taskVideoId) === String(videoProjectId);

      console.log("[VideoEditorView] ✓ Video merge status:", {
        progress,
        status,
        publicationId: currentVideoMerge.publicationId,
        publicationMatches,
        isMerging
      });

      // Update progress
      if (publicationMatches && projectMatches) {
        setMergeProgress(progress);

        // If not yet set as merging and progress < 100 (and not completed/failed/error)
        if (progress > 0 && progress < 100 && status !== "completed" && status !== "failed" && status !== "error" && !isMerging) {
          setIsMerging(true);
        }

        const isCompleted = status === "completed" || status === "success" || status === "finished" || status === "";

        // Handle completion
        if (progress >= 100 && isCompleted && !isMergeComplete) {
          console.log("[VideoEditorView] ✓✓ Video merge completed");
          setIsMerging(false);
          setMergeProgress(100);
          setIsMergeComplete(true);
          showAlert("Video created successfully!", "success");

          if (fetchNotifications) fetchNotifications();
          if (playNotificationSound) playNotificationSound();

          // Notify parent component
          if (onVideoCreated) {
            onVideoCreated();
          }
        }
        // Fallback: If progress is 100 but backend hasn't sent 'completed' status yet
        else if (progress >= 100 && !isMergeComplete) {
          console.log("[VideoEditorView] ✓✓ Video merge reached 100% - triggering fallback completion");
          setIsMerging(false);
          setMergeProgress(100);
          setIsMergeComplete(true);
          showAlert("Video created successfully!", "success");

          if (fetchNotifications) fetchNotifications();
          if (playNotificationSound) playNotificationSound();

          // Notify parent component
          if (onVideoCreated) {
            onVideoCreated();
          }
        }

        // Handle error
        if ((status === "error" || status === "failed") && isMerging) {
          console.error("[VideoEditorView] Video merge failed");
          setIsMerging(false);
          setMergeProgress(0);
          setIsMergeComplete(false);
          showAlert("Error while creating video, please try again", "error");
        }
      }
    }

    // Monitor video_regenerate progress
    // This is for when content or images are being regenerated
    const videoRegenerateTasks = Object.values(taskStatus.video_regenerate || {});
    const currentVideoRegenerate = videoRegenerateTasks.find(t => {
      if (!t) return false;
      const id = t.video_project_id || t.videoProjectId || t.projectId || t.videoId;
      return id && Number(id) === Number(videoProjectId) && Number(t.publicationId) === Number(publicationId);
    });
    
    if (currentVideoRegenerate && currentVideoRegenerate.progress > 0) {
      const progress = Number(currentVideoRegenerate.progress);
      const status = currentVideoRegenerate.status;
      const publicationMatches = !currentVideoRegenerate.publicationId ||
        currentVideoRegenerate.publicationId === publicationId;

      console.log("[VideoEditorView] ✓ Video regenerate status:", {
        progress,
        status,
        publicationId: currentVideoRegenerate.publicationId,
        publicationMatches
      });

      // The actual progress display for regeneration is handled by section-specific states
      // (regeneratingContentIds, applyingSectionIds, etc.)
      // This is just for logging and notifications

      if (publicationMatches && progress >= 100) {
        console.log("[VideoEditorView] ✓✓ Video regenerate completed");

        if (fetchNotifications) {
          fetchNotifications();
        }
        if (playNotificationSound) {
          playNotificationSound();
        }
      }

      if ((status === "error" || status === "failed") && publicationMatches) {
        console.error("[VideoEditorView] Video regenerate failed");
      }
    }
  }, [taskStatus.video_merge, taskStatus.video_regenerate, isMerging, isMergeComplete, publicationId, videoProjectId, fetchNotifications, playNotificationSound, onVideoCreated]);

  // Cleanup when dialog closes
  useEffect(() => {
    if (!open) {
      console.log("[VideoEditorView] Dialog closed, resetting merge states");
      setIsMerging(false);
      setMergeProgress(0);
      setIsMergeComplete(false);
      setAlertOpen(false);
      // Local WebSocket cleanup will be handled by the existing cleanup logic
    }
  }, [open]);


  // Update video volume when it changes
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.volume = volume;
    }
  }, [volume]);

  const fetchVideoContent = async () => {
    setIsLoading(true);
    setError(null);
    try {
      console.log("[VideoEditorView] Fetching content for videoProjectId:", videoProjectId);
      const response = await fetch(
        `${BASE_URL}/video_content?projectId=${videoProjectId}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch video content");
      }

      const data = await response.json();
      console.log("[VideoEditorView] Received data:", data);

      // Handle nested response structure
      const responseData = data.response || data;
      console.log("[VideoEditorView] Response data:", responseData);

      const results = responseData.results || [];
      setPublicationTitle(responseData.publication_title || "");
      setSections(results);
      if (results.length > 0) {
        setSelectedSections(new Set(results.map((_, index) => index)));
      }
    } catch (err) {
      console.error("[VideoEditorView] Error fetching video content:", err);
      setError("Failed to load video content. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const selectedSection = sections[selectedSectionIndex] || null;

  // Get story/voice-over script from llm_response
  const getVoiceOverScript = (section) => {
    if (!section?.llm_response) return "";

    // Handle case where llm_response is an object (not an array)
    if (!Array.isArray(section.llm_response)) {
      return section.llm_response.story ||
        section.llm_response.story2 ||
        section.llm_response.body_text ||
        section.llm_response.bullet_points || "";
    }

    // Support both old format (name/values) and new format (layerName/content)
    const storyItem = section.llm_response.find(item =>
      item.name === "story" || item.name === "story2" ||
      item.layerName === "story" || item.layerName === "story2" ||
      item.layerName === "body_text" || item.layerName === "bullet_points"
    );
    if (storyItem?.values?.length > 0) {
      return storyItem.values[0];
    }
    if (storyItem?.content) {
      return storyItem.content;
    }
    return "";
  };

  // Get current caption based on video time - splits voice-over into sentences
  const getCurrentCaption = (section, currentVideoTime, videoDuration) => {
    const fullScript = getVoiceOverScript(section);
    if (!fullScript || !videoDuration || videoDuration === 0) return "";

    // Split script into sentences (by period, exclamation, or question mark followed by space or end)
    const sentences = fullScript.split(/(?<=[.!?])\s+/).filter(s => s.trim().length > 0);
    if (sentences.length === 0) return "";

    // Calculate time per sentence based on video duration
    const timePerSentence = videoDuration / sentences.length;

    // Find which sentence should be displayed based on current time
    const sentenceIndex = Math.min(
      Math.floor(currentVideoTime / timePerSentence),
      sentences.length - 1
    );

    return sentences[sentenceIndex] || "";
  };

  // Get AI generated images
  const getAiImages = (section) => {
    // Support both old format (images.slots) and new format (images.versions)
    const imagesList = section?.images?.slots || section?.images?.versions || [];
    if (imagesList.length === 0) return [];
    // Support both old format (Ai) and new format (ai)
    return imagesList.filter(slot =>
      slot.imageType === "Ai" || slot.imageType === "ai"
    ).map(slot => {
      const currentUrl = slot.current_url || slot.url;
      return {
        ...slot,
        // Normalize URL: use current_url as primary, fallback to url
        url: currentUrl,
        // Store the actual current_url for comparison
        current_url: currentUrl,
        // Normalize version fields
        selected_version: slot.selected_version ?? slot.image_selected_version ?? 1,
        total_versions: slot.total_versions ?? slot.image_total_versions ?? 1,
        // Map versions array to use consistent url field and preserve is_current flag
        versions: (slot.versions || []).map(v => ({
          ...v,
          url: v.image_url || v.url,
          is_current: v.is_current || (v.image_url || v.url) === currentUrl
        }))
      };
    });
  };

  // Get extracted images (non-AI)
  const getExtractedImages = (section) => {
    // Support both old format (images.slots) and new format (images.versions)
    const imagesList = section?.images?.slots || section?.images?.versions || [];
    if (imagesList.length === 0) return [];
    // Support both old format and new format (nonAi)
    return imagesList.filter(slot =>
      slot.imageType !== "Ai" && slot.imageType !== "ai"
    ).map(slot => {
      const currentUrl = slot.current_url || slot.url;
      return {
        ...slot,
        // Normalize URL: use current_url as primary, fallback to url
        url: currentUrl,
        // Store the actual current_url for comparison
        current_url: currentUrl,
        // Normalize version fields
        selected_version: slot.selected_version ?? slot.image_selected_version ?? 1,
        total_versions: slot.total_versions ?? slot.image_total_versions ?? 1,
        // Map versions array to use consistent url field and preserve is_current flag
        versions: (slot.versions || []).map(v => ({
          ...v,
          url: v.image_url || v.url,
          is_current: v.is_current || (v.image_url || v.url) === currentUrl
        }))
      };
    });
  };

  // Get video URL
  const getVideoUrl = (section) => {
    return section?.video?.url || null;
  };

  // Regenerate section content - calls video_content_regenerate endpoint
  const handleRegenerate = async () => {
    const section = sections[selectedSectionIndex];
    if (!section) return;
    const sequenceId = section.sequence_id;

    setRegeneratingContentIds(prev => new Set(prev).add(sequenceId));
    setRegeneratedContentMap(prev => ({ ...prev, [sequenceId]: null })); // Clear previous for this section
    showAlert("Regenerating content...", "info");
    try {
      const response = await fetch(`${BASE_URL}/video_content_regenerate?sequenceId=${sequenceId}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) throw new Error("Failed to regenerate content");
      const data = await response.json();
      const newContent = data.response || data;
      console.log("[VideoEditorView] Regenerated content:", newContent);

      // Store the regenerated content - user needs to click Apply to apply it
      setRegeneratedContentMap(prev => ({ ...prev, [sequenceId]: newContent }));

      // Update section with is_rendered from response
      setSections(prev => prev.map((s, idx) => {
        if (idx === selectedSectionIndex) {
          return {
            ...s,
            is_rendered: newContent.is_rendered || "no",
            content_total_versions: newContent.content_total_versions || s.content_total_versions,
            content_version_number: newContent.content_version_number || newContent.content_total_versions,
          };
        }
        return s;
      }));

      // Update content version state from regenerate response
      // Determine total versions: use content_total_versions from response, or derive from version number
      const currentTotalVersions = sectionVersions[section.sequence_id]?.length || section.content_total_versions || 1;
      const newVersionNumber = newContent.content_version_number || newContent.content_total_versions;
      const newTotalVersions = newContent.content_total_versions ||
        (newVersionNumber ? Math.max(currentTotalVersions, newVersionNumber) : currentTotalVersions + 1);

      setSectionVersions(prev => ({
        ...prev,
        [section.sequence_id]: Array.from({ length: newTotalVersions }, (_, i) => i + 1)
      }));

      // Set selected version to the new version
      const newVersion = newVersionNumber || newTotalVersions;
      setSelectedVersion(prev => ({
        ...prev,
        [section.sequence_id]: newVersion
      }));

      showAlert("New content generated! Click Apply to update the video.", "success");
    } catch (err) {
      console.error("[VideoEditorView] Error regenerating content:", err);
      showAlert("Failed to regenerate content", "error");
    } finally {
      setRegeneratingContentIds(prev => {
        const next = new Set(prev);
        next.delete(sequenceId);
        return next;
      });
    }
  };

  // Apply regenerated content - calls regenerate_video_part and connects to WebSocket
  const handleApply = async () => {
    const section = sections[selectedSectionIndex];
    const sequenceId = section?.sequence_id;
    // Allow apply if there's regenerated content OR if is_rendered is "no"
    if (!section || (!regeneratedContentMap[sequenceId] && section.is_rendered !== "no")) return;

    setApplyingSectionIds(prev => new Set(prev).add(sequenceId));
    setApplyProgressMap(prev => ({ ...prev, [sequenceId]: 0 }));
    showAlert("Applying changes...", "info");

    try {
      // Call regenerate_video_part endpoint
      const response = await fetch(`${BASE_URL}/regenerate_video_part?sequenceId=${sequenceId}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) throw new Error("Failed to start video regeneration");
      const data = await response.json();
      console.log("[VideoEditorView] Video regeneration started:", data);

      if (data.task_id) {
        // Connect to WebSocket for progress updates
        connectApplyWebSocket(data.task_id, sequenceId);
      } else {
        throw new Error("No task_id received");
      }
    } catch (err) {
      console.error("[VideoEditorView] Error applying changes:", err);
      showAlert("Failed to apply changes", "error");
      setApplyingSectionIds(prev => {
        const next = new Set(prev);
        next.delete(sequenceId);
        return next;
      });
      setApplyProgressMap(prev => ({ ...prev, [sequenceId]: 0 }));
    }
  };

  // Connect to WebSocket for Apply progress
  const connectApplyWebSocket = (taskId, sequenceId) => {
    const wsUrl = BASE_URL.replace(/^http/, "ws");
    const ws = new WebSocket(`${wsUrl}/taskStatus/${taskId}`);
    applyWebSocketRef.current = ws;

    ws.onopen = () => {
      console.log("[VideoEditorView] Apply WebSocket connected");
    };

    ws.onmessage = async (event) => {
      try {
        const message = JSON.parse(event.data);
        console.log("[VideoEditorView] WebSocket message:", message);

        const progress = message.progress || 0;

        // If progress is negative, disconnect WebSocket and don't update UI
        if (progress < 0) {
          console.log("[VideoEditorView] Negative progress received, disconnecting WebSocket");
          ws.close();
          showAlert("Video regeneration encountered an issue", "error");
          setApplyingSectionIds(prev => {
            const next = new Set(prev);
            next.delete(sequenceId);
            return next;
          });
          setApplyProgressMap(prev => ({ ...prev, [sequenceId]: 0 }));
          return;
        }

        setApplyProgressMap(prev => ({ ...prev, [sequenceId]: progress }));

        if (progress >= 100 || message.status === "completed") {
          ws.close();
          // Fetch the updated sequence data
          await fetchUpdatedSequence(sequenceId);
        } else if (message.status === "failed" || message.status === "error") {
          ws.close();
          showAlert("Video regeneration failed", "error");
          setApplyingSectionIds(prev => {
            const next = new Set(prev);
            next.delete(sequenceId);
            return next;
          });
          setApplyProgressMap(prev => ({ ...prev, [sequenceId]: 0 }));
        }
      } catch (err) {
        console.error("[VideoEditorView] Error parsing WebSocket message:", err);
      }
    };

    ws.onerror = (error) => {
      console.error("[VideoEditorView] WebSocket error:", error);
      showAlert("Connection error", "error");
      setApplyingSectionIds(prev => {
        const next = new Set(prev);
        next.delete(sequenceId);
        return next;
      });
      setApplyProgressMap(prev => ({ ...prev, [sequenceId]: 0 }));
    };

    ws.onclose = () => {
      console.log("[VideoEditorView] Apply WebSocket closed");
      applyWebSocketRef.current = null;
    };
  };

  // Fetch updated sequence data after video regeneration completes
  const fetchUpdatedSequence = async (sequenceId) => {
    try {
      const response = await fetch(`${BASE_URL}/get_video_sequence?sequenceId=${sequenceId}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error("Failed to fetch updated sequence");
      const data = await response.json();
      console.log("[VideoEditorView] Updated sequence data:", data);

      // Update the section with new data
      setSections(prev => prev.map((s) => {
        if (s.sequence_id === sequenceId) {
          return {
            ...s,
            ...data,
            llm_response: data.llm_response,
            video: data.video,
            content_version_number: data.content_version_number,
            content_selected_version: data.content_selected_version,
            content_total_versions: data.content_total_versions,
            is_rendered: data.is_rendered || "yes", // Update is_rendered after successful apply
          };
        }
        return s;
      }));

      // Update content version state
      // Derive total versions from response or current state
      const currentTotal = sectionVersions[sequenceId]?.length || 1;
      const responseTotalVersions = data.content_total_versions ||
        Math.max(currentTotal, data.content_version_number || 1, data.content_selected_version || 1);

      setSectionVersions(prev => ({
        ...prev,
        [sequenceId]: Array.from({ length: responseTotalVersions }, (_, i) => i + 1)
      }));
      setSelectedVersion(prev => ({
        ...prev,
        [sequenceId]: data.content_selected_version || data.content_version_number || 1
      }));

      // Update video version state
      if (data.video) {
        setVideoVersions(prev => ({
          ...prev,
          [sequenceId]: Array.from({ length: data.video.video_total_versions || 1 }, (_, i) => i + 1)
        }));
        setSelectedVideoVersion(prev => ({
          ...prev,
          [sequenceId]: data.video.video_selected_version || data.video.video_version_number || 1
        }));
      }

      // Clear regenerated content and reset state
      setRegeneratedContentMap(prev => ({ ...prev, [sequenceId]: null }));
      setApplyingSectionIds(prev => {
        const next = new Set(prev);
        next.delete(sequenceId);
        return next;
      });
      setApplyProgressMap(prev => ({ ...prev, [sequenceId]: 0 }));
      showAlert("Changes applied successfully!", "success");
    } catch (err) {
      console.error("[VideoEditorView] Error fetching updated sequence:", err);
      showAlert("Failed to fetch updated data", "error");
      setApplyingSectionIds(prev => {
        const next = new Set(prev);
        next.delete(sequenceId);
        return next;
      });
      setApplyProgressMap(prev => ({ ...prev, [sequenceId]: 0 }));
    }
  };

  // Cleanup WebSocket on unmount
  useEffect(() => {
    return () => {
      if (applyWebSocketRef.current) {
        applyWebSocketRef.current.close();
      }
      if (mergeWebSocketRef.current) {
        mergeWebSocketRef.current.close();
      }
    };
  }, []);

  const handleClose = () => {
    if (typeof onClose === 'function') {
      onClose();
    }
  };

  // Change content version - rollback to selected version
  const handleVersionChange = async (e) => {
    const version = Number(e.target.value);
    const section = sections[selectedSectionIndex];
    if (!section) return;
    const sequenceId = section.sequence_id;

    // Determine current version - use regenerated content version if it exists, otherwise section version
    const currentVersion = regeneratedContentMap[sequenceId]
      ? (regeneratedContentMap[sequenceId].content_version_number || regeneratedContentMap[sequenceId].content_selected_version)
      : (section.content_selected_version || section.content_version_number);

    // Don't call API if selecting the same version that's currently displayed
    if (version === currentVersion) {
      return;
    }

    setRegeneratingContentIds(prev => new Set(prev).add(sequenceId));
    showAlert("Loading version " + version + "...", "info");

    try {
      const response = await fetch(`${BASE_URL}/video_content_rollback?sequenceId=${section.sequence_id}&versionNumber=${version}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) throw new Error("Failed to rollback to selected version");
      const data = await response.json();
      console.log("[VideoEditorView] Rollback response:", data);

      // Find the updated section from the response
      const responseSection = data.response?.results?.find(r => r.sequence_id === section.sequence_id) || data.response?.results?.[0];

      if (responseSection) {
        // Update the section with all data from the response (content, video, images)
        const updatedSection = {
          ...section,
          // Content updates
          content_selected_version: responseSection.content_selected_version,
          content_total_versions: responseSection.content_total_versions,
          content_version_number: responseSection.content_version_number || responseSection.content_selected_version,
          llm_response: responseSection.llm_response,
          template_name: responseSection.template_name || section.template_name,
          is_rendered: responseSection.is_rendered || section.is_rendered,
          // Video updates
          video: responseSection.video ? {
            ...section.video,
            ...responseSection.video
          } : section.video,
          // Images updates
          images: responseSection.images ? {
            ...section.images,
            ...responseSection.images
          } : section.images
        };

        setSections(prev => prev.map((s) => s.sequence_id === sequenceId ? updatedSection : s));

        // Clear regenerated content when rolling back
        setRegeneratedContentMap(prev => ({ ...prev, [sequenceId]: null }));

        // Update version selectors
        setSectionVersions(prev => ({
          ...prev,
          [section.sequence_id]: Array.from({ length: responseSection.content_total_versions || 1 }, (_, i) => i + 1)
        }));

        setSelectedVersion(prev => ({
          ...prev,
          [section.sequence_id]: responseSection.content_selected_version
        }));

        // Update video versions if available
        if (responseSection.video) {
          setVideoVersions(prev => ({
            ...prev,
            [section.sequence_id]: Array.from({ length: responseSection.video.video_total_versions || 1 }, (_, i) => i + 1)
          }));
          setSelectedVideoVersion(prev => ({
            ...prev,
            [section.sequence_id]: responseSection.video.video_selected_version || responseSection.video.video_version_number
          }));
        }

        showAlert("Version " + version + " loaded successfully!", "success");
      }
    } catch (err) {
      console.error("[VideoEditorView] Error loading version:", err);
      showAlert("Failed to load version " + version, "error");
    } finally {
      setRegeneratingContentIds(prev => {
        const next = new Set(prev);
        next.delete(sequenceId);
        return next;
      });
    }
  };

  // Change video version
  const handleVideoVersionChange = async (e) => {
    const version = Number(e.target.value);
    const section = sections[selectedSectionIndex];
    if (!section) return;
    setIsChangingVideoVersion(true);
    try {
      const response = await fetch(`${BASE_URL}/rollback_video_part?sequenceId=${section.sequence_id}&versionNumber=${version}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) throw new Error("Failed to rollback video to selected version");
      const responseData = await response.json();
      console.log("[VideoEditorView] Video rollback response:", responseData);

      // Extract the sequence data from the nested response structure
      const data = responseData.response?.results?.[0] || responseData;

      // Build complete slots with versions including current_url
      const updatedSlots = data.images?.slots?.map(slot => {
        let allVersions = [...(slot.versions || [])];

        // Add current_url as the selected version if not already in versions
        if (slot.current_url && slot.image_selected_version) {
          const hasSelectedVersion = allVersions.some(v => v.version_number === slot.image_selected_version);
          if (!hasSelectedVersion) {
            allVersions.push({
              version_number: slot.image_selected_version,
              url: slot.current_url,
              created_at: new Date().toISOString()
            });
          }
        }

        // Sort versions by version number
        allVersions.sort((a, b) => a.version_number - b.version_number);

        return {
          ...slot,
          url: slot.current_url,
          selected_version: slot.image_selected_version,
          total_versions: slot.image_total_versions,
          versions: allVersions,
        };
      }) || section.images?.slots || [];

      const updatedSection = {
        ...section,
        // Content updates
        content_version_number: data.content_version_number ?? section.content_version_number,
        content_selected_version: data.content_selected_version ?? section.content_selected_version,
        content_total_versions: data.content_total_versions ?? section.content_total_versions,
        llm_response: data.llm_response || section.llm_response,
        // Video updates
        video: {
          ...section.video,
          url: data.video?.url || section.video?.url,
          video_selected_version: data.video?.video_selected_version,
          video_total_versions: data.video?.video_total_versions,
          video_version_number: data.video?.video_selected_version,
        },
        // Images updates
        images: {
          ...section.images,
          slots: updatedSlots,
        },
      };

      setSections(prev => prev.map((s, idx) => idx === selectedSectionIndex ? updatedSection : s));

      // Update content versions
      if (data.content_total_versions) {
        setSectionVersions(prev => ({
          ...prev,
          [section.sequence_id]: Array.from({ length: data.content_total_versions }, (_, i) => i + 1)
        }));
        setSelectedVersion(prev => ({
          ...prev,
          [section.sequence_id]: data.content_selected_version
        }));
      }

      // Update video versions
      setVideoVersions(prev => ({
        ...prev,
        [section.sequence_id]: Array.from({ length: data.video?.video_total_versions || 1 }, (_, i) => i + 1)
      }));

      setSelectedVideoVersion(prev => ({
        ...prev,
        [section.sequence_id]: data.video?.video_selected_version
      }));

      showAlert("Video version changed successfully", "success");
    } catch (err) {
      console.error("[VideoEditorView] Error rolling back video version:", err);
      showAlert("Failed to change video version", "error");
    } finally {
      setIsChangingVideoVersion(false);
    }
  };

  // Export video
  // Merge Video - sends selected sequence IDs to /merge-video endpoint
  const handleMergeVideo = async () => {
    if (selectedSections.size === 0) {
      showAlert("Please select at least one section to merge", "warning");
      return;
    }

    if (selectedSections.size === 1) {
      showAlert("Please select at least 2 sections to create video", "warning");
      return;
    }

    const sequenceIds = sections
      .filter((_, index) => selectedSections.has(index))
      .map(section => section.sequence_id);

    console.log("[VideoEditorView] Merging sequence IDs:", sequenceIds);
    setIsMerging(true);
    setMergeProgress(0);
    setIsMergeComplete(false);

    // Don't reset global taskStatus here to avoid wiping other active projects.
    // We'll initialize the specific task_id once we receive it.

    try {
      // FastAPI expects the body to be a direct JSON array
      const response = await fetch(`${BASE_URL}/merge-video`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(sequenceIds),
      });

      if (!response.ok) {
        throw new Error("Failed to merge video");
      }

      const data = await response.json();
      console.log("[VideoEditorView] Merge response:", data);

      if (data.task_id) {
        console.log("[VideoEditorView] Connecting to WebSocket with task_id:", data.task_id);
        setupWebSocket(data.task_id, "video_merge", publicationId, videoProjectId, (message, severity) => {
          showAlert(message, severity);
        });
        setActiveTasks(prev => {
          const currentArr = prev.video_merge || [];
          if (!currentArr.includes(videoProjectId)) {
            return { ...prev, video_merge: [...currentArr, videoProjectId] };
          }
          return prev;
        });

        // Initialize global task status so progress is visible on navigation
        setTaskStatus(prev => ({
          ...prev,
          video_merge: {
            ...prev.video_merge,
            [videoProjectId]: {
              progress: 0,
              status: "processing",
              publicationId: Number(publicationId),
              video_project_id: videoProjectId,
              taskId: data.task_id
            }
          }
        }));

        showAlert("Video creation started", "info");
      } else {
        // If no task_id but successful response, merge might have completed immediately
        console.log("[VideoEditorView] Merge completed immediately without task_id.");
        setIsMerging(false);
        setMergeProgress(100);
        setIsMergeComplete(true);
        showAlert("Video created successfully!", "success");
        if (fetchNotifications) fetchNotifications();
        if (playNotificationSound) playNotificationSound();
        if (onVideoCreated) onVideoCreated();
      }
    } catch (err) {
      console.error("[VideoEditorView] Error merging video:", err);
      showAlert("Failed to create video", "error");
      setIsMerging(false);
      setMergeProgress(0);
    }
  };

  // Play merged video - opens dialog which handles fetching
  const handlePlayVideo = () => {
    console.log("[VideoEditorView] handlePlayVideo called", {
      videoProjectId,
      isMergeComplete,
      tokenPresent: !!token,
      selectedSections: selectedSections.size,
    });
    if (!videoProjectId) {
      console.warn("[VideoEditorView] Cannot open merged video dialog: missing videoProjectId", { videoProjectId });
    }
    // Just open the dialog - it will handle fetching the video
    setShowMergedVideoDialog(true);
  };

  // Close merged video dialog
  const handleCloseMergedVideoDialog = () => {
    setShowMergedVideoDialog(false);
  };

  // Play/Pause video
  const handlePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  // Format time from seconds to MM:SS
  const formatTime = (seconds) => {
    if (!seconds || Number.isNaN(seconds)) return "00:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  // Handle fullscreen toggle
  const handleFullscreen = () => {
    if (!videoContainerRef.current) return;

    if (document.fullscreenElement) {
      document.exitFullscreen().catch((err) => {
        console.error("Error exiting fullscreen:", err);
      });
    } else {
      videoContainerRef.current.requestFullscreen().catch((err) => {
        console.error("Error entering fullscreen:", err);
      });
    }
  };
  // Helper to read SSE stream
  const readSSEStream = async (response, sequenceId, setKeysStateFn, setSlotIndexFn, onSuccess) => {
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let currentEvent = null;

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop(); // Keep partial line in buffer

        for (const line of lines) {
          if (line.trim() === '') {
            currentEvent = null;
          } else if (line.startsWith("event: ")) {
            currentEvent = line.substring(7).trim();
          } else if (line.startsWith("data: ")) {
            const dataStr = line.substring(6).trim();
            if (dataStr === "keep-alive") continue;

            try {
              const data = JSON.parse(dataStr);
              if (currentEvent === "status") {
                setRegenerationMessages(prev => ({ ...prev, [sequenceId]: data.message }));
              } else if (currentEvent === "error") {
                throw new Error(data.detail || "Error processing image");
              } else if (currentEvent === "result") {
                if (data.status === "success") {
                  onSuccess(data);
                }
              }
            } catch (err) {
              if (err.message && err.message !== "Unexpected end of JSON input" && !err.message.includes("JSON")) {
                throw err;
              }
            }
          }
        }
      }
    } finally {
      setRegenerationMessages(prev => {
        const next = { ...prev };
        delete next[sequenceId];
        return next;
      });
      setKeysStateFn(prev => {
        const next = new Set(prev);
        next.delete(sequenceId);
        return next;
      });
      if (setSlotIndexFn) {
        setSlotIndexFn(prev => {
          const next = { ...prev };
          delete next[sequenceId];
          return next;
        });
      }
    }
  };

  // Handle image regeneration (for AI images)
  const handleImageRegenerate = async (slotIndex) => {
    const section = sections[selectedSectionIndex];
    if (!section) return;
    const sequenceId = section.sequence_id;

    // Get the currently applied URL from section data BEFORE making the API call
    const currentSlot = section?.images?.slots?.find(s => s.slot_index === slotIndex) ||
      section?.images?.versions?.find(s => s.slot_index === slotIndex);
    const appliedUrlBeforeRegenerate = currentSlot?.current_url || currentSlot?.url;

    setRegeneratingImageIds(prev => new Set(prev).add(sequenceId));
    setRegeneratingSlotIndices(prev => ({ ...prev, [sequenceId]: slotIndex }));
    showAlert("Regenerating image...", "info");

    try {
      const formData = new FormData();
      formData.append("sequenceId", section.sequence_id);
      formData.append("slotIndex", slotIndex);
      formData.append("regenerate", "true");

      const response = await fetch(
        `${BASE_URL}/video_image_regenerate`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        }
      );

      if (!response.ok) throw new Error("Failed to regenerate image");

      await readSSEStream(response, sequenceId, setRegeneratingImageIds, setRegeneratingSlotIndices, (data) => {
        // Handle new multi-slot response format - find the slot for the requested slotIndex
        const slotData = data.slots?.find(s => s.slot_index === slotIndex) || data.slots?.[0];

        // Extract slot-specific data (support both new and old response formats)
        const slotCurrentUrl = slotData?.current_url || data.current_image_url;
        const slotVersions = slotData?.versions || data.versions || [];
        const slotTotalVersions = slotData?.total_versions || data.total_versions;
        const slotSelectedVersion = slotData?.selected_version || data.selected_version;
        const slotImageType = slotData?.imageType || data.imageType;
        const slotSlotIndex = slotData?.slot_index ?? data.slot_index ?? slotIndex;

        // Map versions to consistent format - ensure we have an array
        // Support both 'url' and 'image_url' field names
        const mappedVersions = slotVersions.map(v => ({
          version_number: v.version_number,
          url: v.url || v.image_url,
          created_at: v.created_at
        }));

        // Check if current_url (the applied image) is already in versions array
        const currentInVersions = mappedVersions.find(v => v.url === slotCurrentUrl);

        // If current_url is not in versions, add it with its selected_version number (the applied version)
        if (slotCurrentUrl && !currentInVersions) {
          mappedVersions.push({
            version_number: slotSelectedVersion || 1,  // Use selected_version, not total_versions
            url: slotCurrentUrl,
            created_at: new Date().toISOString()
          });
        }

        // Sort versions by version number (ascending) so they display in order
        mappedVersions.sort((a, b) => a.version_number - b.version_number);

        console.log("[VideoEditorView] Mapped versions after processing:", mappedVersions);

        // Find the newly generated version (highest version number, different from current_url)
        const newlyGeneratedVersion = mappedVersions
          .filter(v => v.url !== slotCurrentUrl)
          .sort((a, b) => b.version_number - a.version_number)[0];

        // The newly generated image URL is from the newly generated version, not current_url
        const newlyGeneratedImageUrl = newlyGeneratedVersion?.url || mappedVersions[mappedVersions.length - 1]?.url;

        // Determine if this is an AI image
        const isAiImage = slotImageType === "Ai" || slotImageType === "ai";

        // Get the actual applied image URL from section data (before regeneration)
        // Use the URL we saved before the API call, or fallback to current_url from API response
        const actualAppliedUrl = appliedUrlBeforeRegenerate || slotCurrentUrl || data.selected_version_url || data.applied_image_url;

        // Update the sections state with new versions data so it persists when switching sections
        const regeneratedSlotIndex = slotSlotIndex;
        setSections(prev => prev.map((s, idx) => {
          if (idx === selectedSectionIndex) {
            const existingSlots = s.images?.slots || [];
            // Find or create the slot for this regenerated image
            const slotExists = existingSlots.some(slot => slot.slot_index === regeneratedSlotIndex);

            let updatedSlots;
            if (slotExists) {
              // Update existing slot with new versions
              updatedSlots = existingSlots.map(slot => {
                if (slot.slot_index === regeneratedSlotIndex) {
                  return {
                    ...slot,
                    versions: mappedVersions,
                    total_versions: slotTotalVersions,
                    imageType: slotImageType || slot.imageType
                  };
                }
                return slot;
              });
            } else {
              // Add new slot
              updatedSlots = [...existingSlots, {
                slot_index: regeneratedSlotIndex,
                current_url: actualAppliedUrl,
                url: actualAppliedUrl,
                versions: mappedVersions,
                total_versions: slotTotalVersions,
                selected_version: slotSelectedVersion,
                imageType: slotImageType || "Ai"
              }];
            }

            return {
              ...s,
              images: {
                ...s.images,
                slots: updatedSlots
              }
            };
          }
          return s;
        }));

        // Store the regenerated image data - user needs to click Apply to apply it
        setRegeneratedImageDataMap(prev => ({
          ...prev,
          [sequenceId]: {
            sequenceId: data.sequenceId || data.sequence_id || sequenceId,
            slotIndex: regeneratedSlotIndex,
            totalVersions: slotTotalVersions,
            currentImageUrl: newlyGeneratedImageUrl, // newly generated image (highest version, not current_url)
            appliedImageUrl: actualAppliedUrl, // actually applied image in video (current_url)
            versions: mappedVersions,
            latestVersion: newlyGeneratedVersion || mappedVersions[mappedVersions.length - 1], // newly generated version object
            isUpload: !isAiImage, // false for AI images (show under AI Generated), true for non-AI
            imageType: slotImageType
          }
        }));

        showAlert("New image generated! Click Apply to update.", "success");
      });

      /*
      const data = await response.json();
      console.log("[VideoEditorView] Image regenerate response:", data);

      // Handle new multi-slot response format - find the slot for the requested slotIndex
      const slotData = data.slots?.find(s => s.slot_index === slotIndex) || data.slots?.[0];

      // Extract slot-specific data (support both new and old response formats)
      const slotCurrentUrl = slotData?.current_url || data.current_image_url;
      const slotVersions = slotData?.versions || data.versions || [];
      const slotTotalVersions = slotData?.total_versions || data.total_versions;
      const slotSelectedVersion = slotData?.selected_version || data.selected_version;
      const slotImageType = slotData?.imageType || data.imageType;
      const slotSlotIndex = slotData?.slot_index ?? data.slot_index ?? slotIndex;

      // Map versions to consistent format - ensure we have an array
      // Support both 'url' and 'image_url' field names
      const mappedVersions = slotVersions.map(v => ({
        version_number: v.version_number,
        url: v.url || v.image_url,
        created_at: v.created_at
      }));

      // Check if current_url (the applied image) is already in versions array
      const currentInVersions = mappedVersions.find(v => v.url === slotCurrentUrl);

      // If current_url is not in versions, add it with its selected_version number (the applied version)
      if (slotCurrentUrl && !currentInVersions) {
        mappedVersions.push({
          version_number: slotSelectedVersion || 1,  // Use selected_version, not total_versions
          url: slotCurrentUrl,
          created_at: new Date().toISOString()
        });
      }

      // Sort versions by version number (ascending) so they display in order
      mappedVersions.sort((a, b) => a.version_number - b.version_number);

      console.log("[VideoEditorView] Mapped versions after processing:", mappedVersions);

      // Find the newly generated version (highest version number, different from current_url)
      const newlyGeneratedVersion = mappedVersions
        .filter(v => v.url !== slotCurrentUrl)
        .sort((a, b) => b.version_number - a.version_number)[0];

      // The newly generated image URL is from the newly generated version, not current_url
      const newlyGeneratedImageUrl = newlyGeneratedVersion?.url || mappedVersions[mappedVersions.length - 1]?.url;

      // Determine if this is an AI image
      const isAiImage = slotImageType === "Ai" || slotImageType === "ai";

      // Get the actual applied image URL from section data (before regeneration)
      // Use the URL we saved before the API call, or fallback to current_url from API response
      const actualAppliedUrl = appliedUrlBeforeRegenerate || slotCurrentUrl || data.selected_version_url || data.applied_image_url;

      // Update the sections state with new versions data so it persists when switching sections
      const regeneratedSlotIndex = slotSlotIndex;
      setSections(prev => prev.map((s, idx) => {
        if (idx === selectedSectionIndex) {
          const existingSlots = s.images?.slots || [];
          // Find or create the slot for this regenerated image
          const slotExists = existingSlots.some(slot => slot.slot_index === regeneratedSlotIndex);

          let updatedSlots;
          if (slotExists) {
            // Update existing slot with new versions
            updatedSlots = existingSlots.map(slot => {
              if (slot.slot_index === regeneratedSlotIndex) {
                return {
                  ...slot,
                  versions: mappedVersions,
                  total_versions: slotTotalVersions,
                  imageType: slotImageType || slot.imageType
                };
              }
              return slot;
            });
          } else {
            // Add new slot
            updatedSlots = [...existingSlots, {
              slot_index: regeneratedSlotIndex,
              current_url: actualAppliedUrl,
              url: actualAppliedUrl,
              versions: mappedVersions,
              total_versions: slotTotalVersions,
              selected_version: slotSelectedVersion,
              imageType: slotImageType || "Ai"
            }];
          }

          return {
            ...s,
            images: {
              ...s.images,
              slots: updatedSlots
            }
          };
        }
        return s;
      }));

      // Store the regenerated image data - user needs to click Apply to apply it
      setRegeneratedImageDataMap(prev => ({
        ...prev,
        [sequenceId]: {
          sequenceId: data.sequenceId || data.sequence_id || sequenceId,
          slotIndex: regeneratedSlotIndex,
          totalVersions: slotTotalVersions,
          currentImageUrl: newlyGeneratedImageUrl, // newly generated image (highest version, not current_url)
          appliedImageUrl: actualAppliedUrl, // actually applied image in video (current_url)
          versions: mappedVersions,
          latestVersion: newlyGeneratedVersion || mappedVersions[mappedVersions.length - 1], // newly generated version object
          isUpload: !isAiImage, // false for AI images (show under AI Generated), true for non-AI
          imageType: slotImageType
        }
      }));

      showAlert("New image generated! Click Apply to update.", "success");
      */
    } catch (err) {
      console.error("[VideoEditorView] Error regenerating image:", err);
      showAlert(err.message || "Failed to regenerate image", "error");
    }
  };

  // Handle image upload (for extracted images section)
  // Can be called with event (from AI images) or with (file, slotIndex) directly (from extracted images)
  const handleImageUpload = async (eventOrFile, providedSlotIndex) => {
    let file;
    let isEventCall = false;

    // Check if called with event object or file directly
    if (eventOrFile?.target?.files) {
      // Called with event object
      isEventCall = true;
      const files = eventOrFile.target.files;
      if (!files || files.length === 0) return;
      file = files[0];
    } else if (eventOrFile instanceof File) {
      // Called with file directly
      file = eventOrFile;
    } else {
      console.error("[VideoEditorView] handleImageUpload called with invalid argument");
      return;
    }

    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB in bytes

    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      showAlert("Image size exceeds 10MB limit. Please upload a smaller image.", "error");
      if (isEventCall) eventOrFile.target.value = "";
      return;
    }

    const section = sections[selectedSectionIndex];
    if (!section) {
      if (isEventCall) eventOrFile.target.value = "";
      return;
    }

    // Use provided slot index or calculate it
    let slotIndex;
    if (providedSlotIndex !== undefined) {
      slotIndex = providedSlotIndex;
    } else {
      // Get the slot index based on all existing image slots (both AI and non-AI)
      const allImages = section?.images?.slots || section?.images?.versions || [];
      // For uploads, we target the extracted images (non-AI) slot
      const extractedImages = allImages.filter(slot =>
        slot.imageType !== "Ai" && slot.imageType !== "ai"
      );
      slotIndex = extractedImages.length > 0
        ? Math.max(...extractedImages.map(img => img.slot_index))
        : (allImages.length > 0 ? Math.max(...allImages.map(img => img.slot_index)) : 0);
    }

    // Get the currently applied URL from section data BEFORE making the API call
    const allImages = section?.images?.slots || section?.images?.versions || [];
    const extractedImages = allImages.filter(slot =>
      slot.imageType !== "Ai" && slot.imageType !== "ai"
    );
    // Look in extracted images first, then all images
    const currentSlot = extractedImages.find(s => s.slot_index === slotIndex) ||
      allImages.find(s => s.slot_index === slotIndex);
    const appliedUrlBeforeUpload = currentSlot?.current_url || currentSlot?.url;

    console.log("[VideoEditorView] Upload - slotIndex:", slotIndex, "currentSlot:", currentSlot, "appliedUrlBeforeUpload:", appliedUrlBeforeUpload);

    const sequenceId = section.sequence_id;
    setRegeneratingImageIds(prev => new Set(prev).add(sequenceId));
    setRegeneratingSlotIndices(prev => ({ ...prev, [sequenceId]: slotIndex }));
    showAlert("Uploading image...", "info");

    try {
      const formData = new FormData();
      formData.append("sequenceId", section.sequence_id);
      formData.append("slotIndex", slotIndex);
      formData.append("imageFile", file);
      formData.append("regenerate", "false");

      const response = await fetch(
        `${BASE_URL}/video_image_regenerate`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        }
      );

      if (!response.ok) throw new Error("Failed to upload image");
      const data = await response.json();
      console.log("[VideoEditorView] Image upload response:", data);

      // Handle new multi-slot response format - find the slot for the requested slotIndex
      const slotData = data.slots?.find(s => s.slot_index === slotIndex) || data.slots?.[0];

      // Extract slot-specific data (support both new and old response formats)
      const slotCurrentUrl = slotData?.current_url || data.current_image_url;
      const slotVersions = slotData?.versions || data.versions || [];
      const slotTotalVersions = slotData?.total_versions || data.total_versions;
      const slotSelectedVersion = slotData?.selected_version || data.selected_version;
      const slotImageType = slotData?.imageType || data.imageType;
      const slotSlotIndex = slotData?.slot_index ?? data.slot_index ?? slotIndex;

      // Map versions to consistent format - ensure we have an array
      // Support both 'url' and 'image_url' field names
      const mappedVersions = slotVersions.map(v => ({
        version_number: v.version_number,
        url: v.url || v.image_url,
        created_at: v.created_at
      }));

      // Check if current_url (the applied image) is already in versions array
      const currentInVersions = mappedVersions.find(v => v.url === slotCurrentUrl);

      // If current_url is not in versions, add it with its selected_version number (the applied version)
      if (slotCurrentUrl && !currentInVersions) {
        mappedVersions.push({
          version_number: slotSelectedVersion || 1,  // Use selected_version, not total_versions
          url: slotCurrentUrl,
          created_at: new Date().toISOString()
        });
      }

      // Sort versions by version number (ascending) so they display in order
      mappedVersions.sort((a, b) => a.version_number - b.version_number);

      console.log("[VideoEditorView] Mapped versions after processing:", mappedVersions);
      console.log("[VideoEditorView] appliedUrlBeforeUpload:", appliedUrlBeforeUpload);
      console.log("[VideoEditorView] slotSelectedVersion:", slotSelectedVersion);
      console.log("[VideoEditorView] slotCurrentUrl:", slotCurrentUrl);

      // Find the newly uploaded version (highest version number, different from applied URL)
      const newlyUploadedVersion = mappedVersions
        .filter(v => v.url !== (appliedUrlBeforeUpload || slotCurrentUrl))
        .sort((a, b) => b.version_number - a.version_number)[0];

      // The newly uploaded image URL is from the newly uploaded version
      const newlyUploadedImageUrl = newlyUploadedVersion?.url || mappedVersions[mappedVersions.length - 1]?.url;

      // Determine if this is an AI image or non-AI (extracted/uploaded) image
      const isAiImage = slotImageType === "Ai" || slotImageType === "ai";

      // Get the actual applied image URL - the one currently in the video BEFORE upload
      // Priority: 1) appliedUrlBeforeUpload (what we captured before upload - this is what's actually in the video)
      //           2) current_url from API if it matches selected_version
      //           3) selected_version_url from API
      //           4) Find version by selected_version number
      let actualAppliedUrl = appliedUrlBeforeUpload;

      // If no URL from before upload, try current_url from API (if it's the selected version)
      if (!actualAppliedUrl && slotCurrentUrl) {
        // Only use current_url as applied if it's not the newly uploaded one
        if (!newlyUploadedVersion || slotCurrentUrl !== newlyUploadedVersion.url) {
          actualAppliedUrl = slotCurrentUrl;
        }
      }

      // If still no URL, try API response
      if (!actualAppliedUrl) {
        actualAppliedUrl = data.selected_version_url;
      }

      // If still no URL, try to find by selected_version number
      if (!actualAppliedUrl && slotSelectedVersion && mappedVersions.length > 0) {
        const selectedVersionObj = mappedVersions.find(v => v.version_number === slotSelectedVersion);
        if (selectedVersionObj) {
          actualAppliedUrl = selectedVersionObj.url;
        }
      }

      // If still no applied URL, try to find the selected version from versions array with flags
      if (!actualAppliedUrl && slotVersions) {
        const selectedVersionFromApi = slotVersions.find(v => v.is_selected || v.selected);
        if (selectedVersionFromApi) {
          actualAppliedUrl = selectedVersionFromApi.url || selectedVersionFromApi.image_url;
        }
      }

      console.log("[VideoEditorView] Final actualAppliedUrl:", actualAppliedUrl);
      console.log("[VideoEditorView] newlyUploadedImageUrl:", newlyUploadedImageUrl);

      // Update the sections state with new versions data so it persists when switching sections
      const uploadedSlotIndex = slotSlotIndex;
      setSections(prev => prev.map((s, idx) => {
        if (idx === selectedSectionIndex) {
          const existingSlots = s.images?.slots || [];
          // Find or create the slot for this uploaded image
          const slotExists = existingSlots.some(slot => slot.slot_index === uploadedSlotIndex);

          let updatedSlots;
          if (slotExists) {
            // Update existing slot with new versions
            updatedSlots = existingSlots.map(slot => {
              if (slot.slot_index === uploadedSlotIndex) {
                return {
                  ...slot,
                  versions: mappedVersions,
                  total_versions: slotTotalVersions,
                  imageType: slotImageType || slot.imageType || "nonAi"
                };
              }
              return slot;
            });
          } else {
            // Add new slot
            updatedSlots = [...existingSlots, {
              slot_index: uploadedSlotIndex,
              current_url: actualAppliedUrl,
              url: actualAppliedUrl,
              versions: mappedVersions,
              total_versions: slotTotalVersions,
              selected_version: slotSelectedVersion,
              imageType: slotImageType || "nonAi"
            }];
          }

          return {
            ...s,
            images: {
              ...s.images,
              slots: updatedSlots
            }
          };
        }
        return s;
      }));

      // Store the uploaded image data - user needs to click Apply to apply it
      setRegeneratedImageDataMap(prev => ({
        ...prev,
        [sequenceId]: {
          sequenceId: data.sequenceId || data.sequence_id || sequenceId,
          slotIndex: uploadedSlotIndex,
          totalVersions: slotTotalVersions,
          currentImageUrl: newlyUploadedImageUrl, // newly uploaded image (highest version, not current_url)
          appliedImageUrl: actualAppliedUrl, // actually applied image in video (current_url or before upload)
          versions: mappedVersions,
          latestVersion: newlyUploadedVersion || mappedVersions[mappedVersions.length - 1], // newly uploaded version object
          isUpload: !isAiImage, // false for AI images (show under AI Generated), true for non-AI (show under Extracted)
          imageType: slotImageType
        }
      }));

      showAlert("Image uploaded! Click Apply to update.", "success");
    } catch (err) {
      console.error("[VideoEditorView] Error uploading image:", err);
      showAlert("Failed to upload image", "error");
    } finally {
      setRegeneratingImageIds(prev => {
        const next = new Set(prev);
        next.delete(sequenceId);
        return next;
      });
      setRegeneratingSlotIndices(prev => {
        const next = { ...prev };
        delete next[sequenceId];
        return next;
      });
      // Reset the input if called with event
      if (isEventCall && eventOrFile?.target) {
        eventOrFile.target.value = "";
      }
    }
  };

  // Show confirmation dialog for applying image
  const showApplyImageConfirmation = () => {
    const sequenceId = selectedSection?.sequence_id;
    if (!regeneratedImageDataMap[sequenceId] && !selectedImageForApplyMap[sequenceId]) return;
    setConfirmDialogAction('applyImage');
    setConfirmDialogOpen(true);
  };

  // Handle selecting an existing image version for applying
  const handleSelectImageForApply = (slotIndex, versionNumber, url, isAi, isCurrentlyApplied, fromUploadedVersions = false, isAlreadySelectedForApply = false) => {
    // Don't allow selecting the currently applied version
    if (isCurrentlyApplied) return;

    const section = sections[selectedSectionIndex];
    if (!section) return;
    const sequenceId = section.sequence_id;

    if (isAlreadySelectedForApply) {
      // Unselect it if it was already selected
      setSelectedImageForApplyMap(prev => {
        const next = { ...prev };
        delete next[sequenceId];
        return next;
      });
      return;
    }

    setSelectedImageForApplyMap(prev => ({
      ...prev,
      [sequenceId]: {
        sequenceId,
        slotIndex,
        versionNumber,
        url,
        isAi
      }
    }));
    // Only clear regeneratedImageDataMap for this section when selecting from existing (non-uploaded) images
    // Don't clear when selecting from uploaded versions to keep showing all versions
    if (!fromUploadedVersions) {
      setRegeneratedImageDataMap(prev => ({ ...prev, [sequenceId]: null }));
    }
  };

  // Handle confirmation dialog close
  const handleConfirmDialogClose = () => {
    setConfirmDialogOpen(false);
    setConfirmDialogAction(null);
  };

  // Handle confirmation dialog confirm
  const handleConfirmDialogConfirm = () => {
    setConfirmDialogOpen(false);
    if (confirmDialogAction === 'applyImage') {
      executeApplyImage();
    }
    setConfirmDialogAction(null);
  };

  // Apply regenerated/uploaded image - calls /apply_image endpoint, then regenerates video
  const executeApplyImage = async () => {
    const section = sections[selectedSectionIndex];
    if (!section) return;
    const sequenceId = section.sequence_id;

    // Use selectedImageForApplyMap if available, otherwise use regeneratedImageDataMap
    const imageToApply = selectedImageForApplyMap[sequenceId] || regeneratedImageDataMap[sequenceId];
    if (!imageToApply) return;

    setApplyingImageIds(prev => new Set(prev).add(sequenceId));
    setApplyProgressMap(prev => ({ ...prev, [sequenceId]: 0 }));
    showAlert("Applying image changes...", "info");

    try {
      // Get version number from the appropriate source
      let versionNumber;
      const selection = selectedImageForApplyMap[sequenceId];
      if (selection) {
        versionNumber = selection.versionNumber;
      } else {
        const regenData = regeneratedImageDataMap[sequenceId];
        const latestVersion = regenData?.latestVersion;
        versionNumber = latestVersion?.version_number || latestVersion?.image_version || regenData?.totalVersions;
      }

      const slotIndex = imageToApply.slotIndex;

      setApplyingSlotInfo(prev => ({
        ...prev,
        [sequenceId]: { slotIndex, versionNumber }
      }));

      // Step 1: Call apply_image endpoint with new multi-slot format
      // Backend expects: sequenceId as query param, selections as body Dict[str, int]
      // selections maps slot_index (as string) to version_number
      const selections = { [slotIndex.toString()]: versionNumber };

      const applyResponse = await fetch(
        `${BASE_URL}/apply_image?sequenceId=${sequenceId}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(selections),
        }
      );

      if (!applyResponse.ok) throw new Error("Failed to apply image");
      const applyData = await applyResponse.json();
      console.log("[VideoEditorView] Apply image response:", applyData);

      setApplyProgressMap(prev => ({ ...prev, [sequenceId]: 10 }));
      showAlert("Image applied! Regenerating video...", "info");

      // Step 2: Call regenerate_video_part endpoint
      const regenerateResponse = await fetch(
        `${BASE_URL}/regenerate_video_part?sequenceId=${sequenceId}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!regenerateResponse.ok) throw new Error("Failed to start video regeneration");
      const regenerateData = await regenerateResponse.json();
      console.log("[VideoEditorView] Regenerate video response:", regenerateData);

      const taskId = regenerateData.task_id;
      if (!taskId) throw new Error("No task ID received");

      setApplyProgressMap(prev => ({ ...prev, [sequenceId]: 20 }));

      // Step 3: Connect to WebSocket for progress
      const wsUrl = `${BASE_URL.replace(/^http/, "ws")}/taskStatus/${taskId}`;
      console.log("[VideoEditorView] Connecting to WebSocket:", wsUrl);

      const ws = new WebSocket(wsUrl);

      await new Promise((resolve, reject) => {
        ws.onopen = () => {
          console.log("[VideoEditorView] WebSocket connected for task:", taskId);
        };

        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            console.log("[VideoEditorView] WebSocket message:", data);

            // Update progress (scale from 20 to 90)
            if (data.progress !== undefined) {
              const scaledProgress = 20 + Math.round(data.progress * 0.7);
              setApplyProgressMap(prev => ({ ...prev, [sequenceId]: scaledProgress }));
            }

            // Check for completion
            if (data.status === "completed" || data.progress === 100) {
              ws.close();
              resolve();
            } else if (data.status === "failed" || data.status === "error") {
              ws.close();
              reject(new Error(data.message || "Video regeneration failed"));
            }
          } catch (e) {
            console.error("[VideoEditorView] Error parsing WebSocket message:", e);
          }
        };

        ws.onerror = (error) => {
          console.error("[VideoEditorView] WebSocket error:", error);
          ws.close();
          reject(new Error("WebSocket connection error"));
        };

        ws.onclose = () => {
          console.log("[VideoEditorView] WebSocket closed");
        };

        // Timeout after 5 minutes
        setTimeout(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.close();
            reject(new Error("Video regeneration timeout"));
          }
        }, 300000);
      });

      setApplyProgressMap(prev => ({ ...prev, [sequenceId]: 95 }));

      // Step 4: Fetch updated sequence data
      const sequenceResponse = await fetch(
        `${BASE_URL}/get_video_sequence?sequenceId=${sequenceId}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!sequenceResponse.ok) throw new Error("Failed to fetch updated sequence");
      const sequenceData = await sequenceResponse.json();
      console.log("[VideoEditorView] Get video sequence response:", sequenceData);

      setApplyProgressMap(prev => ({ ...prev, [sequenceId]: 100 }));

      // Step 5: Update section with all data from response
      setSections(prev => prev.map((s, idx) => {
        if (idx === selectedSectionIndex) {
          // Map slots to our expected format
          const updatedSlots = sequenceData.slots?.map(slot => {
            const existingSlot = s.images?.slots?.find(es => es.slot_index === slot.slot_index);

            // Build complete versions array including current_url as the selected version
            let allVersions = [...(slot.versions || [])];

            // Add current_url as the selected version if not already in versions
            if (slot.current_url && slot.selected_version) {
              const hasSelectedVersion = allVersions.some(v => v.version_number === slot.selected_version);
              if (!hasSelectedVersion) {
                allVersions.push({
                  version_number: slot.selected_version,
                  image_url: slot.current_url,
                  url: slot.current_url,
                  created_at: new Date().toISOString()
                });
              }
            }

            // Sort versions by version number
            allVersions.sort((a, b) => a.version_number - b.version_number);

            // Map to consistent format
            const mappedVersions = allVersions.map(v => ({
              version_number: v.version_number,
              url: v.image_url || v.url,
              image_url: v.image_url || v.url,
              created_at: v.created_at
            }));

            return {
              slot_index: slot.slot_index,
              current_url: slot.current_url,
              url: slot.current_url,
              imageType: slot.imageType || existingSlot?.imageType || "nonAi",
              selected_version: slot.selected_version,
              total_versions: slot.total_versions,
              versions: mappedVersions,
            };
          }) || s.images?.slots || [];

          return {
            ...s,
            // Content updates
            content_version_number: sequenceData.content_version_number,
            content_selected_version: sequenceData.content_selected_version,
            content_total_versions: sequenceData.content_total_versions,
            llm_response: sequenceData.llm_response || s.llm_response,
            template_name: sequenceData.template_name || s.template_name,
            // Video updates
            video: sequenceData.video ? {
              ...s.video,
              sequence_id: sequenceData.video.sequence_id,
              url: sequenceData.video.url,
              video_version_number: sequenceData.video.video_version_number,
              video_selected_version: sequenceData.video.video_selected_version,
              video_total_versions: sequenceData.video.video_total_versions,
            } : s.video,
            // Images updates
            images: {
              ...s.images,
              slots: updatedSlots,
            },
          };
        }
        return s;
      }));

      // Update mapping states
      const section = sections[selectedSectionIndex];

      if (sequenceData.content_total_versions) {
        setSectionVersions(prev => ({
          ...prev,
          [section.sequence_id]: Array.from({ length: sequenceData.content_total_versions }, (_, i) => i + 1)
        }));
        setSelectedVersion(prev => ({
          ...prev,
          [section.sequence_id]: sequenceData.content_selected_version
        }));
      }

      // Update video versions
      if (sequenceData.video) {
        setVideoVersions(prev => ({
          ...prev,
          [section.sequence_id]: Array.from({ length: sequenceData.video.video_total_versions || 1 }, (_, i) => i + 1)
        }));
        setSelectedVideoVersion(prev => ({
          ...prev,
          [section.sequence_id]: sequenceData.video.video_selected_version || sequenceData.video.video_version_number
        }));
      }

      // Clear regenerated image data and selected image for this section
      setRegeneratedImageDataMap(prev => ({ ...prev, [sequenceId]: null }));
      setSelectedImageForApplyMap(prev => ({ ...prev, [sequenceId]: null }));
      showAlert("Image applied and video regenerated successfully!", "success");
    } catch (err) {
      console.error("[VideoEditorView] Error applying image:", err);
      showAlert(err.message || "Failed to apply image", "error");
    } finally {
      setApplyingImageIds(prev => {
        const next = new Set(prev);
        next.delete(sequenceId);
        return next;
      });
      setApplyingSlotInfo(prev => {
        const next = { ...prev };
        delete next[sequenceId];
        return next;
      });
      setApplyProgressMap(prev => ({ ...prev, [sequenceId]: 0 }));
    }
  };

  // Handle image click for preview
  const handleImagePreview = (imageUrl, imageInfo) => {
    setPreviewImage({ url: imageUrl, info: imageInfo });
  };

  // Close image preview
  const handleClosePreview = () => {
    setPreviewImage(null);
  };

  console.log("[VideoEditorView] Render - open:", open, "videoProjectId:", videoProjectId, "token:", !!token);

  if (!open) {
    console.log("[VideoEditorView] Not rendering - open is false");
    return null;
  }

  return (
    <Box sx={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 1300, backgroundColor: "#fff", fontFamily: "'Inter', sans-serif" }}>
      <SidebarTopbar>
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%", backgroundColor: "#f5f7fa", fontFamily: "'Inter', sans-serif" }}>
          {/* Top Bar */}
          <Box sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "12px 24px",
            backgroundColor: "#fff",
            borderBottom: "1px solid #e0e0e0"
          }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <IconButton onClick={handleClose} size="small">
                <ArrowBackIcon />
              </IconButton>
              {publicationTitle && (
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#333", maxWidth: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {publicationTitle}
                </Typography>
              )}
              {professionalType && (
                <Chip
                  label={`Audience: ${professionalType}`}
                  variant="outlined"
                  sx={{
                    borderColor: PRIMARY_BLUE,
                    color: PRIMARY_BLUE,
                    fontWeight: 600,
                    fontSize: "13px",
                    height: "32px",
                    borderRadius: "8px",
                    backgroundColor: "#e8f0fe",
                    px: 1,
                    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
                  }}
                />
              )}
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Tooltip title={isMergeComplete ? "" : "Select at least 2 sections to create the video"} arrow>
                <span>
                  <Button
                    variant="contained"
                    onClick={isMergeComplete ? handlePlayVideo : handleMergeVideo}
                    disabled={isMerging || (!isMergeComplete && selectedSections.size < 2)}
                    startIcon={
                      isMerging ? (
                        <CircularProgress size={16} sx={{ color: PRIMARY_BLUE }} />
                      ) : (
                        isMergeComplete ? <PlayArrowIcon /> : null
                      )
                    }
                    sx={{
                      backgroundColor: isMerging ? "#E3EAFC" : PRIMARY_BLUE,
                      color: isMerging ? PRIMARY_BLUE : "#fff",
                      textTransform: "none",
                      "&:hover": { backgroundColor: isMerging ? "#D0DBFA" : "#163aab" },
                      "&.Mui-disabled": isMerging ? {
                        backgroundColor: "#E3EAFC",
                        color: PRIMARY_BLUE
                      } : {}
                    }}
                  >
                    {isMerging ? `Creating... ${mergeProgress}%` : (isMergeComplete ? "Play Video" : "Create Video")}
                  </Button>
                </span>
              </Tooltip>
            </Box>
          </Box>

          {/* Main Content */}
          <Box sx={{ display: "flex", flex: 1, overflow: "hidden" }}>
            {/* Left Panel - Video Preview */}
            <Box sx={{
              width: "50%",
              padding: "16px 20px",
              display: "flex",
              flexDirection: "column",
              gap: 1
            }}>
              {/* Preview Label with Video Version Selector */}
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#333" }}>
                  Preview
                </Typography>
                {selectedSection && videoVersions[selectedSection.sequence_id]?.length >= 1 && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Select
                      size="small"
                      value={selectedVideoVersion[selectedSection.sequence_id] || 1}
                      onChange={handleVideoVersionChange}
                      disabled={isChangingVideoVersion || applyingSectionIds.has(selectedSection?.sequence_id)}
                      sx={{ minWidth: 60, "& .MuiSelect-select": { py: 0.5, px: 1 } }}
                    >
                      {videoVersions[selectedSection.sequence_id]?.map(v => (
                        <MenuItem key={v} value={v}>v{v}</MenuItem>
                      ))}
                    </Select>
                  </Box>
                )}
              </Box>

              {/* Video Player */}
              <Box
                ref={videoContainerRef}
                sx={{
                  position: "relative",
                  backgroundColor: "#000",
                  borderRadius: "8px",
                  overflow: "hidden",
                  width: "90%",
                  margin: "0 auto",
                  paddingTop: "50.625%", // 16:9 aspect ratio at 90% width
                }}
              >
                <Box sx={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0 }}>
                  {/* Video Content Rendering */}
                  {(() => {
                    if (isLoading) {
                      return (
                        <Box sx={{
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                          transform: "translate(-50%, -50%)",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: 2
                        }}>
                          <CircularProgress sx={{ color: "#fff" }} />
                          <Typography sx={{ color: "#fff" }}>Loading video...</Typography>
                        </Box>
                      );
                    }
                    if (selectedSection && getVideoUrl(selectedSection)) {
                      return (
                        <>
                          <video
                            ref={videoRef}
                            src={getVideoUrl(selectedSection)}
                            style={{ width: "100%", height: "100%", objectFit: "contain" }}
                            onPlay={() => setIsPlaying(true)}
                            onPause={() => setIsPlaying(false)}
                            onEnded={() => setIsPlaying(false)}
                            onTimeUpdate={(e) => setCurrentTime(e.target.currentTime)}
                            onLoadedMetadata={(e) => setDuration(e.target.duration)}
                            muted={isMuted}
                          >
                            <track kind="captions" />
                          </video>
                          {/* Caption Overlay - shows current sentence synced with video time */}
                          {showCaptions && isPlaying && getCurrentCaption(selectedSection, currentTime, duration) && (
                            <Box
                              sx={{
                                position: "absolute",
                                bottom: "80px",
                                left: "50%",
                                transform: "translateX(-50%)",
                                maxWidth: "90%",
                                backgroundColor: "rgba(0, 0, 0, 0.75)",
                                color: "#fff",
                                padding: "8px 16px",
                                borderRadius: "4px",
                                textAlign: "center",
                                zIndex: 10,
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  fontSize: "14px",
                                  lineHeight: 1.5,
                                  wordWrap: "break-word",
                                }}
                              >
                                {getCurrentCaption(selectedSection, currentTime, duration)}
                              </Typography>
                            </Box>
                          )}
                        </>
                      );
                    }
                    return (
                      <Box sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        color: "#fff"
                      }}>
                        No video available
                      </Box>
                    );
                  })()}

                  {/* Video Controls Overlay */}
                  {selectedSection && getVideoUrl(selectedSection) && (
                    <Box sx={{
                      position: "absolute",
                      bottom: 0,
                      left: 0,
                      right: 0,
                      background: "linear-gradient(transparent, rgba(0,0,0,0.7))",
                      padding: "20px 16px 12px",
                    }}>
                      {/* Progress Bar */}
                      <Box
                        sx={{
                          height: 4,
                          backgroundColor: "rgba(255,255,255,0.3)",
                          borderRadius: 2,
                          mb: 1,
                          cursor: "pointer"
                        }}
                        onClick={(e) => {
                          if (videoRef.current && duration) {
                            const rect = e.currentTarget.getBoundingClientRect();
                            const clickX = e.clientX - rect.left;
                            const newTime = (clickX / rect.width) * duration;
                            videoRef.current.currentTime = newTime;
                          }
                        }}
                      >
                        <Box sx={{
                          height: "100%",
                          width: `${duration ? (currentTime / duration) * 100 : 0}%`,
                          backgroundColor: PRIMARY_BLUE,
                          borderRadius: 2
                        }} />
                      </Box>

                      {/* Controls */}
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <Typography sx={{ color: "#fff", fontSize: 12 }}>
                          {formatTime(currentTime)}
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <IconButton
                            size="small"
                            sx={{ color: "#fff" }}
                            onClick={() => {
                              if (videoRef.current) {
                                videoRef.current.currentTime = Math.max(0, videoRef.current.currentTime - 10);
                              }
                            }}
                          >
                            <Box component="span" sx={{ fontSize: 16 }}>⏪</Box>
                          </IconButton>
                          <IconButton
                            onClick={handlePlayPause}
                            sx={{
                              backgroundColor: PRIMARY_BLUE,
                              color: "#fff",
                              "&:hover": { backgroundColor: "#163aab" }
                            }}
                          >
                            {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
                          </IconButton>
                          <IconButton
                            size="small"
                            sx={{ color: "#fff" }}
                            onClick={() => {
                              if (videoRef.current) {
                                videoRef.current.currentTime = Math.min(duration, videoRef.current.currentTime + 10);
                              }
                            }}
                          >
                            <Box component="span" sx={{ fontSize: 16 }}>⏩</Box>
                          </IconButton>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Typography sx={{ color: "#fff", fontSize: 12 }}>
                            {formatTime(duration)}
                          </Typography>
                          <Box 
                            onMouseEnter={() => setIsVolumeHovered(true)}
                            onMouseLeave={() => setIsVolumeHovered(false)}
                            sx={{ 
                              display: "flex", 
                              alignItems: "center", 
                              width: isVolumeHovered ? 110 : 34, 
                              transition: "width 0.3s ease", 
                              overflow: "hidden" 
                            }}
                          >
                            <IconButton 
                              size="small" 
                              sx={{ color: isMuted || volume === 0 ? PRIMARY_BLUE : "#fff", flexShrink: 0 }}
                              onClick={() => {
                                if (isMuted) {
                                  setIsMuted(false);
                                  if (volume === 0) setVolume(1);
                                } else {
                                  setIsMuted(true);
                                }
                              }}
                            >
                              {isMuted || volume === 0 ? <VolumeOffIcon fontSize="small" /> : <VolumeUpIcon fontSize="small" />}
                            </IconButton>
                            <Slider
                              size="small"
                              min={0}
                              max={1}
                              step={0.01}
                              value={isMuted ? 0 : volume}
                              onChange={(e, newValue) => {
                                setVolume(newValue);
                                if (newValue > 0 && isMuted) {
                                  setIsMuted(false);
                                } else if (newValue === 0 && !isMuted) {
                                  setIsMuted(true);
                                }
                              }}
                              sx={{
                                color: PRIMARY_BLUE,
                                ml: 1,
                                mr: 1,
                                width: 60,
                                opacity: isVolumeHovered ? 1 : 0,
                                transition: "opacity 0.3s ease",
                                "& .MuiSlider-thumb": { width: 12, height: 12 }
                              }}
                            />
                          </Box>
                          <Tooltip title={showCaptions ? "Hide Captions" : "Show Captions"} arrow>
                            <IconButton
                              size="small"
                              sx={{ color: showCaptions ? PRIMARY_BLUE : "#fff" }}
                              onClick={() => setShowCaptions(!showCaptions)}
                            >
                              {showCaptions ? <ClosedCaptionIcon fontSize="small" /> : <ClosedCaptionDisabledIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                          <IconButton
                            size="small"
                            sx={{ color: "#fff" }}
                            onClick={handleFullscreen}
                          >
                            <Box component="span" sx={{ fontSize: 14 }}>⛶</Box>
                          </IconButton>
                        </Box>
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>
              {/* Spacing below video to match above */}
              <Box sx={{ height: 16 }} />
            </Box>

            {/* Right Panel - Content/Media */}
            <Box sx={{
              width: "50%",
              backgroundColor: "#fff",
              borderLeft: "1px solid #e0e0e0",
              display: "flex",
              flexDirection: "column"
            }}>
              {/* Tabs */}
              <Box sx={{ borderBottom: "1px solid #e0e0e0", display: "flex", justifyContent: "space-between", alignItems: "center", px: 2 }}>
                <Tabs value={activeTab} onChange={handleTabChange}>
                  <Tab label="Content" sx={{ textTransform: "none", fontWeight: 600 }} />
                  {/* Only show Media tab if section has images */}
                  {selectedSection && (getAiImages(selectedSection).length > 0 || getExtractedImages(selectedSection).length > 0) && (
                    <Tab label="Media" sx={{ textTransform: "none", fontWeight: 600 }} />
                  )}
                </Tabs>
              </Box>

              {/* Tab Content */}
              <Box sx={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
                {(() => {
                  if (isLoading) {
                    return (
                      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                        <CircularProgress />
                      </Box>
                    );
                  }
                  if (error) {
                    return (
                      <Box sx={{ textAlign: "center", color: "red", p: 3 }}>{error}</Box>
                    );
                  }
                  if (activeTab === 0) {
                    /* Content Tab */
                    // Determine which content to display - regenerated or current
                    const displayContent = regeneratedContentMap[selectedSection?.sequence_id] || selectedSection;
                    const isShowingRegenerated = !!regeneratedContentMap[selectedSection?.sequence_id];
                    // Show Apply button if there's regenerated content OR if is_rendered is "no"
                    const shouldShowApplyButton = isShowingRegenerated || (selectedSection?.is_rendered === "no");

                    // Helper to get content from regenerated or current section
                    const getDisplayHeading = () => {
                      if (!displayContent?.llm_response) return displayContent?.template_name || "";

                      // Handle case where llm_response is an object (not an array)
                      if (!Array.isArray(displayContent.llm_response)) {
                        return displayContent.llm_response.topic_name ||
                          displayContent.llm_response.heading ||
                          displayContent.llm_response.title ||
                          displayContent.llm_response.section_heading ||
                          displayContent?.template_name || "";
                      }

                      const headingItem = displayContent.llm_response.find(item =>
                        item.name === "topic_name" ||
                        item.name === "heading" ||
                        item.name === "title" ||
                        item.name === "section_heading"
                      );
                      if (headingItem?.values?.length > 0) {
                        return headingItem.values[0];
                      }
                      return displayContent?.template_name || "";
                    };

                    const getDisplayContentText = () => {
                      if (!displayContent?.llm_response) return [];

                      // Handle case where llm_response is an object (not an array)
                      if (!Array.isArray(displayContent.llm_response)) {
                        const excludeKeys = ["story", "story2", "topic_name", "heading", "title", "section_heading", "conclusion_title"];
                        return Object.entries(displayContent.llm_response)
                          .filter(([key, value]) => !excludeKeys.includes(key) && value)
                          .map(([name, value]) => ({ name, values: [String(value)] }));
                      }

                      const contentItems = displayContent.llm_response.filter(item => {
                        const name = item.name || item.layerName;
                        return name !== "story" &&
                          name !== "story2" &&
                          name !== "topic_name" &&
                          name !== "heading" &&
                          name !== "title" &&
                          name !== "section_heading" &&
                          name !== "conclusion_title";
                      });
                      // Return objects with name and values for display
                      return contentItems.map(item => {
                        const name = item.name || item.layerName || "";
                        const values = item.values && Array.isArray(item.values) ? item.values :
                          item.content ? [item.content] : [];
                        return { name, values };
                      }).filter(item => item.values.length > 0);
                    };

                    const getDisplayVoiceOver = () => {
                      if (!displayContent?.llm_response) return "";

                      // Handle case where llm_response is an object (not an array)
                      if (!Array.isArray(displayContent.llm_response)) {
                        return displayContent.llm_response.story ||
                          displayContent.llm_response.story2 || "";
                      }

                      const storyItem = displayContent.llm_response.find(item => item.name === "story" || item.name === "story2");
                      if (storyItem?.values?.length > 0) {
                        return storyItem.values[0];
                      }
                      return "";
                    };

                    return (
                      <>
                        {/* Scrollable Content Area */}
                        <Box sx={{ flex: 1, overflow: "auto", p: 3 }}>
                          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                            {/* Section Heading Label with Version Selector and Regenerate Button on right */}
                            <Box>
                              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                                <Typography variant="overline" sx={{ color: "#444", letterSpacing: 1, fontWeight: 600, fontSize: "0.75rem" }}>
                                  SECTION HEADING
                                </Typography>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                  {selectedSection?.template_name !== "References" && selectedSection?.template_name !== "Publication Title" && (
                                    <Tooltip title="Credits required: 0.1" arrow placement="top">
                                      <Button
                                        size="small"
                                        startIcon={<AutorenewIcon sx={{ fontSize: 16 }} />}
                                        onClick={handleRegenerate}
                                        disabled={regeneratingContentIds.has(selectedSection?.sequence_id) || !selectedSection || applyingSectionIds.has(selectedSection?.sequence_id)}
                                        sx={{
                                          backgroundColor: "#f0f4ff",
                                          color: PRIMARY_BLUE,
                                          textTransform: "none",
                                          fontSize: "0.8rem",
                                          "&:hover": { backgroundColor: "#e0e8ff" }
                                        }}
                                      >
                                        {regeneratingContentIds.has(selectedSection?.sequence_id) ? "Regenerating..." : "Regenerate"}
                                      </Button>
                                    </Tooltip>
                                  )}
                                  {/* Version Selector - Always show even with 1 version */}
                                  {selectedSection && sectionVersions[selectedSection.sequence_id]?.length >= 1 && (
                                    <Select
                                      size="small"
                                      value={
                                        regeneratedContentMap[selectedSection?.sequence_id]
                                          ? (regeneratedContentMap[selectedSection.sequence_id].content_version_number || regeneratedContentMap[selectedSection.sequence_id].content_selected_version)
                                          : (selectedVersion[selectedSection.sequence_id] || selectedSection.content_selected_version || 1)
                                      }
                                      onChange={handleVersionChange}
                                      disabled={regeneratingContentIds.has(selectedSection?.sequence_id) || applyingSectionIds.has(selectedSection?.sequence_id)}
                                      sx={{ minWidth: 60, "& .MuiSelect-select": { py: 0.5, px: 1 } }}
                                    >
                                      {sectionVersions[selectedSection.sequence_id]?.map(v => (
                                        <MenuItem key={v} value={v}>v{v}</MenuItem>
                                      ))}
                                    </Select>
                                  )}
                                </Box>
                              </Box>

                              {/* Section Heading Content */}
                              <Typography variant="subtitle1" sx={{ color: "#333" }}>
                                {displayContent ? getDisplayHeading() : "No section selected"}
                              </Typography>
                            </Box>

                            {/* Content Text */}
                            <Box>
                              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                <Typography variant="overline" sx={{ color: "#444", letterSpacing: 1, fontWeight: 600, fontSize: "0.75rem" }}>
                                  CONTENT TEXT
                                </Typography>
                              </Box>
                              <Box sx={{
                                mt: 1,
                                p: 0,
                                borderRadius: 1
                              }}>
                                {displayContent && getDisplayContentText().map((item, idx) => (
                                  <Box key={`content-${idx}`} sx={{ mb: 2 }}>
                                    <Typography variant="caption" sx={{
                                      color: PRIMARY_BLUE,
                                      fontWeight: 600,
                                      textTransform: "uppercase",
                                      letterSpacing: 0.5,
                                      fontSize: "0.7rem"
                                    }}>
                                      {item.name.replace(/_/g, " ")}
                                    </Typography>
                                    {item.values.map((text, valIdx) => (
                                      <Typography key={`val-${valIdx}`} variant="body2" sx={{ color: "#333", lineHeight: 1.7, mt: 0.5 }}>
                                        {text}
                                      </Typography>
                                    ))}
                                  </Box>
                                ))}
                                {displayContent && getDisplayContentText().length === 0 && (
                                  <Typography variant="body2" sx={{ color: "#888", lineHeight: 1.7 }}>
                                    No content available
                                  </Typography>
                                )}
                              </Box>
                            </Box>

                            {/* Voice-over Script */}
                            {displayContent && getDisplayVoiceOver() && (
                              <Box>
                                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                  <Typography variant="overline" sx={{ color: "#444", letterSpacing: 1, fontWeight: 600, fontSize: "0.75rem" }}>
                                    VOICE-OVER SCRIPT
                                  </Typography>
                                </Box>
                                <Box sx={{
                                  mt: 1,
                                  p: 2,
                                  backgroundColor: "#f8f9fa",
                                  borderRadius: 2,
                                  display: "flex",
                                  gap: 2
                                }}>
                                  <MicIcon sx={{ color: PRIMARY_BLUE, mt: 0.5 }} />
                                  <Typography variant="body2" sx={{ color: "#333", lineHeight: 1.7 }}>
                                    {getDisplayVoiceOver()}
                                  </Typography>
                                </Box>
                              </Box>
                            )}
                          </Box>
                        </Box>

                        {/* Sticky Footer - New Version Preview on left, Apply Button on right */}
                        {shouldShowApplyButton && (
                          <Box sx={{
                            borderTop: "1px solid #e0e0e0",
                            backgroundColor: "#fff",
                            p: 2,
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            flexShrink: 0
                          }}>
                            <Typography variant="body2" sx={{
                              color: PRIMARY_BLUE,
                              fontWeight: 500,
                              fontSize: "0.85rem"
                            }}>
                              {regeneratedContentMap[selectedSection?.sequence_id] ? "New Version (Preview)" : "Content not applied to video"}
                            </Typography>
                            <Tooltip title={applyingSectionIds.has(selectedSection?.sequence_id) ? "Your changes are applying. Please wait" : "Apply your new version to update the text in the video"} arrow>
                              <span>
                                <Button
                                  variant="contained"
                                  size="small"
                                  onClick={handleApply}
                                  disabled={applyingSectionIds.has(selectedSection?.sequence_id)}
                                  sx={{
                                    backgroundColor: applyingSectionIds.has(selectedSection?.sequence_id) ? "#E3EAFC" : PRIMARY_BLUE,
                                    color: applyingSectionIds.has(selectedSection?.sequence_id) ? PRIMARY_BLUE : "#fff",
                                    textTransform: "none",
                                    minWidth: 80,
                                    fontSize: "0.85rem",
                                    fontWeight: 600,
                                    py: 0.75,
                                    boxShadow: "none",
                                    "&:hover": {
                                      backgroundColor: applyingSectionIds.has(selectedSection?.sequence_id) ? "#E3EAFC" : "#163aab",
                                      boxShadow: "none"
                                    },
                                    "&.Mui-disabled": applyingSectionIds.has(selectedSection?.sequence_id) ? {
                                      backgroundColor: "#E3EAFC",
                                      color: PRIMARY_BLUE
                                    } : {}
                                  }}
                                >
                                  {applyingSectionIds.has(selectedSection?.sequence_id) ? (
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                      <CircularProgress
                                        size={18}
                                        variant="determinate"
                                        value={applyProgressMap[selectedSection?.sequence_id] || 0}
                                        sx={{ color: PRIMARY_BLUE }}
                                      />
                                      <span>{applyProgressMap[selectedSection?.sequence_id] || 0}%</span>
                                    </Box>
                                  ) : (
                                    "Apply"
                                  )}
                                </Button>
                              </span>
                            </Tooltip>
                          </Box>
                        )}
                      </>
                    );
                  }
                  /* Media Tab */
                  return (
                    <Box sx={{ flex: 1, overflow: "auto", p: 3 }}>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                        {/* AI Generated Images - only show if there are AI images */}
                        {selectedSection && getAiImages(selectedSection).length > 0 && (
                          <Box>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                              <Typography variant="overline" sx={{ color: "#666", letterSpacing: 1 }}>
                                AI GENERATED IMAGES
                              </Typography>
                              <Box sx={{ display: "flex", gap: 1 }}>
                                <Tooltip title="Credits required: 0.1" arrow placement="top">
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={regeneratingImageIds.has(selectedSection?.sequence_id) ? <CircularProgress size={12} /> : <AutorenewIcon sx={{ fontSize: 14 }} />}
                                    onClick={() => {
                                      const aiImages = getAiImages(selectedSection);
                                      if (aiImages.length > 0) {
                                        handleImageRegenerate(aiImages[0].slot_index);
                                      }
                                    }}
                                    disabled={regeneratingImageIds.has(selectedSection?.sequence_id) || applyingImageIds.has(selectedSection?.sequence_id)}
                                    sx={{
                                      color: PRIMARY_BLUE,
                                      borderColor: PRIMARY_BLUE,
                                      textTransform: "none",
                                      fontSize: "0.7rem",
                                      py: 0.25,
                                      "&:hover": { borderColor: "#163aab", backgroundColor: "rgba(30, 77, 227, 0.04)" },
                                      "&.Mui-disabled": { borderColor: "#ccc", color: "#999" }
                                    }}
                                  >
                                    {regeneratingImageIds.has(selectedSection?.sequence_id) ? "..." : "Regenerate"}
                                  </Button>
                                </Tooltip>
                                <Tooltip title="Max 10MB" arrow placement="top">
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => !regeneratingImageIds.has(selectedSection?.sequence_id) && imageUploadRef.current?.click()}
                                    disabled={regeneratingImageIds.has(selectedSection?.sequence_id) || applyingImageIds.has(selectedSection?.sequence_id)}
                                    sx={{
                                      color: PRIMARY_BLUE,
                                      borderColor: PRIMARY_BLUE,
                                      textTransform: "none",
                                      fontSize: "0.7rem",
                                      py: 0.25,
                                      "&:hover": { borderColor: "#163aab", backgroundColor: "rgba(30, 77, 227, 0.04)" },
                                      "&.Mui-disabled": { borderColor: "#ccc", color: "#999" }
                                    }}
                                  >
                                    Upload
                                  </Button>
                                </Tooltip>
                                <Button
                                  variant="contained"
                                  size="small"
                                  onClick={showApplyImageConfirmation}
                                  disabled={applyingImageIds.has(selectedSection?.sequence_id) || (
                                    (!regeneratedImageDataMap[selectedSection?.sequence_id] || regeneratedImageDataMap[selectedSection?.sequence_id].isUpload) &&
                                    (!selectedImageForApplyMap[selectedSection?.sequence_id] || !selectedImageForApplyMap[selectedSection?.sequence_id].isAi)
                                  )}
                                  sx={{
                                    backgroundColor: applyingImageIds.has(selectedSection?.sequence_id) ? "#E3EAFC" : PRIMARY_BLUE,
                                    color: applyingImageIds.has(selectedSection?.sequence_id) ? PRIMARY_BLUE : "#fff",
                                    textTransform: "none",
                                    fontSize: "0.7rem",
                                    py: 0.25,
                                    minWidth: 60,
                                    "&:hover": { backgroundColor: applyingImageIds.has(selectedSection?.sequence_id) ? "#E3EAFC" : "#163aab" },
                                    "&.Mui-disabled": { backgroundColor: "#E3EAFC", color: "#999" }
                                  }}
                                >
                                  {applyingImageIds.has(selectedSection?.sequence_id) ? (
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                      <CircularProgress
                                        size={14}
                                        variant="determinate"
                                        value={applyProgressMap[selectedSection?.sequence_id] || 0}
                                        sx={{ color: PRIMARY_BLUE }}
                                      />
                                      <span>{applyProgressMap[selectedSection?.sequence_id] || 0}%</span>
                                    </Box>
                                  ) : "Apply"}
                                </Button>
                                {/* Hidden file input for upload */}
                                <input
                                  ref={imageUploadRef}
                                  type="file"
                                  accept="image/*"
                                  style={{ display: "none" }}
                                  onChange={handleImageUpload}
                                />
                              </Box>
                            </Box>
                            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                              {/* Show all versions from regenerated image data if available */}
                              {regeneratedImageDataMap[selectedSection?.sequence_id] && !regeneratedImageDataMap[selectedSection?.sequence_id].isUpload && regeneratedImageDataMap[selectedSection?.sequence_id].versions?.map((version) => {
                                const isNewlyGenerated = version.url === regeneratedImageDataMap[selectedSection?.sequence_id].currentImageUrl;
                                const isAppliedImage = version.url === regeneratedImageDataMap[selectedSection?.sequence_id].appliedImageUrl;
                                const isSelected = regeneratedImageDataMap[selectedSection?.sequence_id].latestVersion?.version_number === version.version_number;
                                const shouldHighlight = isSelected || isNewlyGenerated || isAppliedImage;
                                return (
                                  <Box
                                    key={`regen-${version.version_number}`}
                                    onClick={() => {
                                      // Don't allow selecting the currently applied version
                                      if (isAppliedImage) return;
                                      // Select this version for applying
                                      setRegeneratedImageDataMap(prev => ({
                                        ...prev,
                                        [selectedSection?.sequence_id]: {
                                          ...prev[selectedSection?.sequence_id],
                                          latestVersion: version
                                        }
                                      }));
                                    }}
                                    sx={{
                                      position: "relative",
                                      width: 120,
                                      height: 100,
                                      borderRadius: 2,
                                      overflow: "hidden",
                                      border: shouldHighlight ? `2px solid ${PRIMARY_BLUE}` : "2px solid #e0e0e0",
                                      backgroundColor: shouldHighlight ? "#e8f0ff" : "#fff",
                                      cursor: isAppliedImage ? "default" : "pointer",
                                      "&:hover": isAppliedImage ? {} : { borderColor: PRIMARY_BLUE, opacity: 0.9 }
                                    }}
                                  >
                                    <Box sx={{
                                      position: "absolute",
                                      top: 4,
                                      left: 4,
                                      backgroundColor: shouldHighlight ? PRIMARY_BLUE : "rgba(0,0,0,0.6)",
                                      color: "#fff",
                                      fontSize: 9,
                                      px: 0.75,
                                      py: 0.25,
                                      borderRadius: 1,
                                      zIndex: 1
                                    }}>
                                      v{version.version_number}{isNewlyGenerated ? " (New)" : isAppliedImage ? " (Current)" : ""}
                                    </Box>
                                    <img
                                      src={version.url}
                                      alt={`Version ${version.version_number}`}
                                      style={{ width: "100%", height: "70px", objectFit: "cover", cursor: "pointer" }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleImagePreview(version.url, { type: "AI", version: version.version_number });
                                      }}
                                    />
                                    <Box sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      py: 0.5,
                                      backgroundColor: shouldHighlight ? "#e8f0ff" : "#f5f5f5",
                                    }}>
                                      <Typography variant="caption" sx={{ color: shouldHighlight ? PRIMARY_BLUE : "#666", fontSize: "0.65rem" }}>
                                        {isAppliedImage ? "(Applied)" : isSelected ? "Click Apply" : "Click to Select"}
                                      </Typography>
                                    </Box>
                                  </Box>
                                )
                              })}
                              {/* Show existing AI images - always show with version selection */}
                              {!regeneratedImageDataMap[selectedSection?.sequence_id] && selectedSection && getAiImages(selectedSection).map((image) => {
                                // URL and versions are now normalized by getAiImages
                                const currentImageUrl = image.current_url; // This is the actual current_url from slot (what's in the video)
                                const selectedVer = image.selected_version;
                                const totalVers = image.total_versions;
                                const slotIdx = image.slot_index ?? image.slotIndex;

                                // Build all versions array including current_url
                                const allVersions = [];
                                const versionsFromApi = image.versions || [];
                                // Check if current_url exists in versions array by comparing URLs
                                const currentInVersions = versionsFromApi.find(v => v.url === currentImageUrl || v.is_current === true);

                                // If current_url is NOT in versions, add it as the applied version
                                if (!currentInVersions && currentImageUrl) {
                                  allVersions.push({
                                    version_number: selectedVer,
                                    url: currentImageUrl,
                                    isCurrentlyApplied: true
                                  });
                                }

                                // Add versions from API, marking the one that matches current_url as applied
                                versionsFromApi.forEach(v => {
                                  allVersions.push({
                                    version_number: v.version_number,
                                    url: v.url,
                                    // Mark as applied if: URL matches current_url OR is_current flag is true
                                    isCurrentlyApplied: v.url === currentImageUrl || v.is_current === true
                                  });
                                });

                                allVersions.sort((a, b) => a.version_number - b.version_number);

                                // If no versions, show single image with regenerate only
                                if (allVersions.length === 0) {
                                  return (
                                    <Box
                                      key={`ai-${slotIdx}-single`}
                                      sx={{
                                        position: "relative",
                                        width: 120,
                                        height: 100,
                                        borderRadius: 2,
                                        overflow: "hidden",
                                        border: `2px solid ${PRIMARY_BLUE}`,
                                      }}
                                    >
                                      <Box sx={{
                                        position: "absolute",
                                        top: 4,
                                        left: 4,
                                        backgroundColor: PRIMARY_BLUE,
                                        color: "#fff",
                                        fontSize: 9,
                                        px: 0.75,
                                        py: 0.25,
                                        borderRadius: 1,
                                        zIndex: 1
                                      }}>
                                        v{selectedVer} (Current)
                                      </Box>
                                      <img
                                        src={currentImageUrl}
                                        alt={`Generated v${selectedVer}`}
                                        style={{ width: "100%", height: "70px", objectFit: "cover", cursor: "pointer" }}
                                        onClick={() => handleImagePreview(currentImageUrl, { type: "AI", version: selectedVer, totalVersions: totalVers })}
                                      />
                                      {(regeneratingImageIds.has(selectedSection?.sequence_id) && regeneratingSlotIndices[selectedSection?.sequence_id] === slotIdx || 
                                        applyingImageIds.has(selectedSection?.sequence_id)) && (
                                        <Box sx={{
                                          position: "absolute", top: 0, left: 0, right: 0, height: "100%",
                                          backgroundColor: "rgba(0,0,0,0.7)", display: "flex", flexDirection: "column",
                                          alignItems: "center", justifyContent: "center", color: "#fff", p: 1, textAlign: "center", zIndex: 2
                                        }}>
                                          <CircularProgress size={20} sx={{ color: "#fff", mb: 0.5 }} />
                                          <Typography sx={{ fontSize: "9px", lineHeight: 1.1 }}>
                                            {regenerationMessages[selectedSection?.sequence_id] || (applyingImageIds.has(selectedSection?.sequence_id) ? "Applying..." : "Processing...")}
                                          </Typography>
                                        </Box>
                                      )}
                                      <Box sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        py: 0.5,
                                        backgroundColor: "#f5f5f5",
                                        borderTop: "1px solid #e0e0e0"
                                      }}>
                                        <Button
                                          size="small"
                                          startIcon={regeneratingImageIds.has(selectedSection?.sequence_id) && regeneratingSlotIndices[selectedSection?.sequence_id] === slotIdx ?
                                            <CircularProgress size={12} /> :
                                            <AutorenewIcon sx={{ fontSize: 14 }} />
                                          }
                                          onClick={() => handleImageRegenerate(slotIdx)}
                                          disabled={regeneratingImageIds.has(selectedSection?.sequence_id) || applyingImageIds.has(selectedSection?.sequence_id)}
                                          sx={{
                                            color: PRIMARY_BLUE,
                                            textTransform: "none",
                                            fontSize: "0.7rem",
                                            py: 0,
                                            minWidth: "auto"
                                          }}
                                        >
                                          {regeneratingImageIds.has(selectedSection?.sequence_id) && regeneratingSlotIndices[selectedSection?.sequence_id] === slotIdx ? "..." : "Regenerate"}
                                        </Button>
                                      </Box>
                                    </Box>
                                  );
                                }

                                // Show all versions with selection capability
                                return (
                                  <React.Fragment key={`ai-slot-${slotIdx}`}>
                                    {allVersions.map((version) => {
                                      const isCurrentlyApplied = version.isCurrentlyApplied;
                                      const isSelectedForApply = selectedImageForApplyMap[selectedSection?.sequence_id]?.slotIndex === slotIdx &&
                                        selectedImageForApplyMap[selectedSection?.sequence_id]?.versionNumber === version.version_number &&
                                        selectedImageForApplyMap[selectedSection?.sequence_id]?.isAi;

                                      return (
                                        <Box
                                          key={`ai-${slotIdx}-v${version.version_number}`}
                                          onClick={() => handleSelectImageForApply(slotIdx, version.version_number, version.url, true, isCurrentlyApplied, false, isSelectedForApply)}
                                          sx={{
                                            position: "relative",
                                            width: 120,
                                            height: 100,
                                            borderRadius: 2,
                                            overflow: "hidden",
                                            border: isCurrentlyApplied ? `2px solid ${PRIMARY_BLUE}` :
                                              isSelectedForApply ? `2px solid ${PRIMARY_BLUE}` : "2px solid #e0e0e0",
                                            backgroundColor: isSelectedForApply ? "#e8f0ff" : "#fff",
                                            cursor: isCurrentlyApplied ? "default" : "pointer",
                                            opacity: isCurrentlyApplied ? 1 : 0.9,
                                            "&:hover": !isCurrentlyApplied ? { borderColor: PRIMARY_BLUE, opacity: 1 } : {}
                                          }}
                                        >
                                          <Box sx={{
                                            position: "absolute",
                                            top: 4,
                                            left: 4,
                                            backgroundColor: isCurrentlyApplied ? PRIMARY_BLUE :
                                              isSelectedForApply ? PRIMARY_BLUE : "rgba(0,0,0,0.6)",
                                            color: "#fff",
                                            fontSize: 9,
                                            px: 0.75,
                                            py: 0.25,
                                            borderRadius: 1,
                                            zIndex: 1
                                          }}>
                                            v{version.version_number}{isCurrentlyApplied ? " (Current)" : ""}
                                          </Box>
                                          <Box sx={{
                                            position: "absolute",
                                            top: 4,
                                            right: 4,
                                            backgroundColor: "rgba(0,0,0,0.5)",
                                            borderRadius: "50%",
                                            p: 0.25,
                                            display: "flex",
                                            cursor: "pointer",
                                            zIndex: 2,
                                            "&:hover": { backgroundColor: PRIMARY_BLUE }
                                          }}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleImagePreview(version.url, { type: "AI", version: version.version_number, totalVersions: totalVers });
                                          }}>
                                            <ZoomInIcon sx={{ color: "#fff", fontSize: 14 }} />
                                          </Box>
                                          <img
                                            src={version.url}
                                            alt={`Generated v${version.version_number}`}
                                            style={{ width: "100%", height: "70px", objectFit: "cover", cursor: "pointer" }}
                                          />
                                          {(() => {
                                            const isBeingRegenerated = regeneratingImageIds.has(selectedSection?.sequence_id) && regeneratingSlotIndices[selectedSection?.sequence_id] === slotIdx && isCurrentlyApplied;
                                            const isBeingApplied = applyingImageIds.has(selectedSection?.sequence_id) && applyingSlotInfo[selectedSection?.sequence_id]?.slotIndex === slotIdx && applyingSlotInfo[selectedSection?.sequence_id]?.versionNumber === version.version_number;
                                            
                                            if (isBeingRegenerated || isBeingApplied) {
                                              return (
                                                <Box sx={{
                                                  position: "absolute", top: 0, left: 0, right: 0, height: "100%",
                                                  backgroundColor: "rgba(0,0,0,0.7)", display: "flex", flexDirection: "column",
                                                  alignItems: "center", justifyContent: "center", color: "#fff", p: 1, textAlign: "center", zIndex: 2
                                                }}>
                                                  <CircularProgress size={20} sx={{ color: "#fff", mb: 0.5 }} />
                                                  <Typography sx={{ fontSize: "9px", lineHeight: 1.1 }}>
                                                    {regenerationMessages[selectedSection?.sequence_id] || (isBeingApplied ? "Applying..." : "Processing...")}
                                                  </Typography>
                                                </Box>
                                              );
                                            }
                                            return null;
                                          })()}
                                          <Box sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            py: 0.5,
                                            backgroundColor: isCurrentlyApplied ? "#e8f0ff" : isSelectedForApply ? "#e8f0ff" : "#f5f5f5",
                                            borderTop: "1px solid #e0e0e0"
                                          }}>
                                            <Typography variant="caption" sx={{ color: isCurrentlyApplied ? PRIMARY_BLUE : isSelectedForApply ? PRIMARY_BLUE : "#666", fontSize: "0.65rem" }}>
                                              {isCurrentlyApplied ? "(Applied)" : isSelectedForApply ? "Click Apply" : "Click to Select"}
                                            </Typography>
                                          </Box>
                                        </Box>
                                      );
                                    })}
                                  </React.Fragment>
                                );
                              })}
                            </Box>
                          </Box>
                        )}

                        {/* Extracted Images - only show if there are extracted images */}
                        {selectedSection && getExtractedImages(selectedSection).length > 0 && (
                          <Box>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                              <Typography variant="overline" sx={{ color: "#666", letterSpacing: 1 }}>
                                EXTRACTED IMAGES
                              </Typography>
                              <Box sx={{ display: "flex", gap: 1 }}>
                                <Tooltip title="Max 10MB" arrow placement="top">
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => document.getElementById('extracted-image-upload-input').click()}
                                    disabled={regeneratingImageIds.has(selectedSection?.sequence_id) || applyingImageIds.has(selectedSection?.sequence_id)}
                                    sx={{
                                      color: PRIMARY_BLUE,
                                      borderColor: PRIMARY_BLUE,
                                      textTransform: "none",
                                      fontSize: "0.7rem",
                                      py: 0.25,
                                      "&:hover": { borderColor: "#163aab", backgroundColor: "rgba(30, 77, 227, 0.04)" },
                                      "&.Mui-disabled": { borderColor: "#ccc", color: "#999" }
                                    }}
                                  >
                                    Upload
                                  </Button>
                                </Tooltip>
                                <Button
                                  variant="contained"
                                  size="small"
                                  onClick={showApplyImageConfirmation}
                                  disabled={applyingImageIds.has(selectedSection?.sequence_id) || (
                                    (!regeneratedImageDataMap[selectedSection?.sequence_id] || !regeneratedImageDataMap[selectedSection?.sequence_id].isUpload) &&
                                    (!selectedImageForApplyMap[selectedSection?.sequence_id] || selectedImageForApplyMap[selectedSection?.sequence_id].isAi)
                                  )}
                                  sx={{
                                    backgroundColor: applyingImageIds.has(selectedSection?.sequence_id) ? "#E3EAFC" : PRIMARY_BLUE,
                                    color: applyingImageIds.has(selectedSection?.sequence_id) ? PRIMARY_BLUE : "#fff",
                                    textTransform: "none",
                                    fontSize: "0.7rem",
                                    py: 0.25,
                                    minWidth: 60,
                                    "&:hover": { backgroundColor: applyingImageIds.has(selectedSection?.sequence_id) ? "#E3EAFC" : "#163aab" },
                                    "&.Mui-disabled": { backgroundColor: "#E3EAFC", color: "#999" }
                                  }}
                                >
                                  {applyingImageIds.has(selectedSection?.sequence_id) && ((selectedImageForApplyMap[selectedSection?.sequence_id] && !selectedImageForApplyMap[selectedSection?.sequence_id].isAi) || regeneratedImageDataMap[selectedSection?.sequence_id]?.isUpload) ? (
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                      <CircularProgress
                                        size={14}
                                        variant="determinate"
                                        value={applyProgressMap[selectedSection?.sequence_id] || 0}
                                        sx={{ color: PRIMARY_BLUE }}
                                      />
                                      <span>{applyProgressMap[selectedSection?.sequence_id] || 0}%</span>
                                    </Box>
                                  ) : "Apply"}
                                </Button>
                                {/* Hidden file input for extracted images upload */}
                                <input
                                  type="file"
                                  id="extracted-image-upload-input"
                                  accept="image/*"
                                  style={{ display: "none" }}
                                  onChange={(e) => {
                                    const file = e.target.files[0];
                                    if (file && selectedSection) {
                                      // Calculate max slot_index from extracted images for this section
                                      const extractedImages = getExtractedImages(selectedSection);
                                      let maxSlotIndex = -1;
                                      extractedImages.forEach(img => {
                                        if (img.slot_index !== undefined && img.slot_index > maxSlotIndex) {
                                          maxSlotIndex = img.slot_index;
                                        }
                                      });
                                      const slotIndex = maxSlotIndex >= 0 ? maxSlotIndex : 0;
                                      handleImageUpload(file, slotIndex);
                                    }
                                    e.target.value = '';
                                  }}
                                />
                              </Box>
                            </Box>
                            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                              {/* Show uploaded versions from regeneratedImageData if available (for extracted/non-AI uploads) */}
                              {regeneratedImageDataMap[selectedSection?.sequence_id] && regeneratedImageDataMap[selectedSection?.sequence_id].isUpload && regeneratedImageDataMap[selectedSection?.sequence_id].versions?.map((version) => {
                                const isNewlyUploaded = version.url === regeneratedImageDataMap[selectedSection?.sequence_id].currentImageUrl;
                                const isAppliedImage = version.url === regeneratedImageDataMap[selectedSection?.sequence_id].appliedImageUrl;
                                const isSelected = regeneratedImageDataMap[selectedSection?.sequence_id].latestVersion?.version_number === version.version_number;
                                const shouldHighlight = isSelected || isNewlyUploaded || isAppliedImage;
                                return (
                                  <Box
                                    key={`upload-${version.version_number}`}
                                    onClick={() => {
                                      // Don't allow selecting the currently applied version
                                      if (isAppliedImage) return;
                                      // Select this version for applying
                                      setRegeneratedImageDataMap(prev => ({
                                        ...prev,
                                        [selectedSection?.sequence_id]: {
                                          ...prev[selectedSection?.sequence_id],
                                          latestVersion: version
                                        }
                                      }));
                                    }}
                                    sx={{
                                      position: "relative",
                                      width: 120,
                                      height: 100,
                                      borderRadius: 2,
                                      overflow: "hidden",
                                      border: shouldHighlight ? `2px solid ${PRIMARY_BLUE}` : "2px solid #e0e0e0",
                                      backgroundColor: shouldHighlight ? "#e8f0ff" : "#fff",
                                      cursor: isAppliedImage ? "default" : "pointer",
                                      "&:hover": isAppliedImage ? {} : { borderColor: PRIMARY_BLUE, opacity: 0.9 }
                                    }}
                                  >
                                    <Box sx={{
                                      position: "absolute",
                                      top: 4,
                                      left: 4,
                                      backgroundColor: shouldHighlight ? PRIMARY_BLUE : "rgba(0,0,0,0.6)",
                                      color: "#fff",
                                      fontSize: 9,
                                      px: 0.75,
                                      py: 0.25,
                                      borderRadius: 1,
                                      zIndex: 1
                                    }}>
                                      v{version.version_number}{isNewlyUploaded ? " (New)" : isAppliedImage ? " (Current)" : ""}
                                    </Box>
                                    <Box sx={{
                                      position: "absolute",
                                      top: 4,
                                      right: 4,
                                      backgroundColor: "rgba(0,0,0,0.5)",
                                      borderRadius: "50%",
                                      p: 0.25,
                                      display: "flex",
                                      cursor: "pointer",
                                      zIndex: 2,
                                      "&:hover": { backgroundColor: PRIMARY_BLUE }
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleImagePreview(version.url, { type: "Uploaded", version: version.version_number });
                                    }}>
                                      <ZoomInIcon sx={{ color: "#fff", fontSize: 14 }} />
                                    </Box>
                                    <img
                                      src={version.url}
                                      alt={`Version ${version.version_number}`}
                                      style={{ width: "100%", height: "70px", objectFit: "cover", cursor: "pointer" }}
                                    />
                                    {(() => {
                                      const isBeingApplied = applyingImageIds.has(selectedSection?.sequence_id) && applyingSlotInfo[selectedSection?.sequence_id]?.slotIndex === regeneratedImageDataMap[selectedSection?.sequence_id]?.slotIndex && applyingSlotInfo[selectedSection?.sequence_id]?.versionNumber === version.version_number;
                                      
                                      if (isBeingApplied) {
                                        return (
                                          <Box sx={{
                                            position: "absolute", top: 0, left: 0, right: 0, height: "100%",
                                            backgroundColor: "rgba(0,0,0,0.7)", display: "flex", flexDirection: "column",
                                            alignItems: "center", justifyContent: "center", color: "#fff", p: 1, textAlign: "center", zIndex: 2
                                          }}>
                                            <CircularProgress size={20} sx={{ color: "#fff", mb: 0.5 }} />
                                            <Typography sx={{ fontSize: "9px", lineHeight: 1.1 }}>
                                              Applying...
                                            </Typography>
                                          </Box>
                                        );
                                      }
                                      return null;
                                    })()}
                                    <Box sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      py: 0.5,
                                      backgroundColor: shouldHighlight ? "#e8f0ff" : "#f5f5f5",
                                    }}>
                                      <Typography variant="caption" sx={{ color: shouldHighlight ? PRIMARY_BLUE : "#666", fontSize: "0.65rem" }}>
                                        {isAppliedImage ? "(Applied)" : isSelected ? "Click Apply" : "Click to Select"}
                                      </Typography>
                                    </Box>
                                  </Box>
                                )
                              })}
                              {/* Show existing extracted images - hide when showing uploaded versions */}
                              {!regeneratedImageDataMap[selectedSection?.sequence_id]?.isUpload && selectedSection && getExtractedImages(selectedSection).map((image) => {
                                // URL and versions are now normalized by getExtractedImages
                                const currentImageUrl = image.current_url; // This is the actual current_url from slot (what's in the video)
                                const totalVers = image.total_versions;
                                const selectedVer = image.selected_version;

                                // Build all versions array including current_url
                                const allVersions = [];

                                // Check if current_url exists in versions array by comparing URLs or is_current flag
                                const versionsFromApi = image.versions || [];
                                const currentInVersions = versionsFromApi.find(v => v.url === currentImageUrl || v.is_current === true);

                                // If current_url is NOT in versions, add it as the applied version
                                if (!currentInVersions && currentImageUrl) {
                                  allVersions.push({
                                    version_number: selectedVer,
                                    url: currentImageUrl,
                                    isCurrent: true
                                  });
                                }

                                // Add all versions from the API, marking the one that matches current_url as applied
                                versionsFromApi.forEach(v => {
                                  allVersions.push({
                                    version_number: v.version_number,
                                    url: v.url,
                                    // Mark as applied if: URL matches current_url OR is_current flag is true
                                    isCurrent: v.url === currentImageUrl || v.is_current === true
                                  });
                                });

                                // Sort by version number descending (newest first)
                                allVersions.sort((a, b) => b.version_number - a.version_number);

                                // If no versions to show, display the current image directly
                                if (allVersions.length === 0) {
                                  if (!currentImageUrl) return null;
                                  return (
                                    <Box
                                      key={`extracted-${image.slot_index}-single`}
                                      sx={{
                                        position: "relative",
                                        width: 120,
                                        height: 100,
                                        borderRadius: 2,
                                        overflow: "hidden",
                                        border: `2px solid ${PRIMARY_BLUE}`,
                                        backgroundColor: "#e8f0ff",
                                        cursor: "default"
                                      }}
                                    >
                                      {totalVers > 1 && (
                                        <Box sx={{
                                          position: "absolute",
                                          top: 4,
                                          left: 4,
                                          backgroundColor: PRIMARY_BLUE,
                                          color: "#fff",
                                          fontSize: 9,
                                          px: 0.75,
                                          py: 0.25,
                                          borderRadius: 1,
                                          zIndex: 1
                                        }}>
                                          v{selectedVer}/{totalVers} (Current)
                                        </Box>
                                      )}
                                      <Box sx={{
                                        position: "absolute",
                                        top: 4,
                                        right: 4,
                                        backgroundColor: "rgba(0,0,0,0.5)",
                                        borderRadius: "50%",
                                        p: 0.25,
                                        display: "flex",
                                        cursor: "pointer",
                                        zIndex: 2,
                                        "&:hover": { backgroundColor: PRIMARY_BLUE }
                                      }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleImagePreview(currentImageUrl, { type: "Extracted", version: selectedVer, totalVersions: totalVers });
                                      }}>
                                        <ZoomInIcon sx={{ color: "#fff", fontSize: 14 }} />
                                      </Box>
                                      <img
                                        src={currentImageUrl}
                                        alt={`From publication v${selectedVer}`}
                                        style={{ width: "100%", height: "100%", objectFit: "cover", cursor: "pointer" }}
                                      />
                                      {(() => {
                                        const isBeingRegenerated = regeneratingImageIds.has(selectedSection?.sequence_id) && regeneratingSlotIndices[selectedSection?.sequence_id] === image.slot_index;
                                        const isBeingApplied = applyingImageIds.has(selectedSection?.sequence_id) && applyingSlotInfo[selectedSection?.sequence_id]?.slotIndex === image.slot_index;
                                        if (isBeingRegenerated || isBeingApplied) {
                                          return (
                                            <Box sx={{
                                              position: "absolute", top: 0, left: 0, right: 0, height: "100%",
                                              backgroundColor: "rgba(0,0,0,0.7)", display: "flex", flexDirection: "column",
                                              alignItems: "center", justifyContent: "center", color: "#fff", p: 1, textAlign: "center", zIndex: 2
                                            }}>
                                              <CircularProgress size={20} sx={{ color: "#fff", mb: 0.5 }} />
                                              <Typography sx={{ fontSize: "9px", lineHeight: 1.1 }}>
                                                {regenerationMessages[selectedSection?.sequence_id] || (isBeingApplied ? "Applying..." : "Processing...")}
                                              </Typography>
                                            </Box>
                                          );
                                        }
                                        return null;
                                      })()}
                                    </Box>
                                  );
                                }

                                // Show all versions for this slot - use React.Fragment to return multiple elements
                                return (
                                  <React.Fragment key={`extracted-slot-${image.slot_index}`}>
                                    {allVersions.map((version) => {
                                      const isVersionSelected = selectedImageForApplyMap[selectedSection?.sequence_id] &&
                                        !selectedImageForApplyMap[selectedSection?.sequence_id].isAi &&
                                        selectedImageForApplyMap[selectedSection?.sequence_id].slotIndex === image.slot_index &&
                                        selectedImageForApplyMap[selectedSection?.sequence_id].versionNumber === version.version_number;

                                      return (
                                        <Box
                                          key={`extracted-${image.slot_index}-v${version.version_number}`}
                                          onClick={() => {
                                            // Don't allow selecting the current version
                                            if (version.isCurrent) return;
                                            // Select this version for applying
                                            handleSelectImageForApply(
                                              image.slot_index,
                                              version.version_number,
                                              version.url,
                                              false, // isAi = false for extracted
                                              isVersionSelected
                                            );
                                          }}
                                          sx={{
                                            position: "relative",
                                            width: 120,
                                            height: 100,
                                            borderRadius: 2,
                                            overflow: "hidden",
                                            border: version.isCurrent ? `2px solid ${PRIMARY_BLUE}` : isVersionSelected ? `2px solid ${PRIMARY_BLUE}` : "2px solid #e0e0e0",
                                            backgroundColor: version.isCurrent ? "#e8f0ff" : isVersionSelected ? "#e8f0ff" : "#fff",
                                            cursor: version.isCurrent ? "default" : "pointer",
                                            "&:hover": {
                                              borderColor: PRIMARY_BLUE,
                                              opacity: version.isCurrent ? 1 : 0.9
                                            }
                                          }}
                                        >
                                          <Box sx={{
                                            position: "absolute",
                                            top: 4,
                                            left: 4,
                                            backgroundColor: version.isCurrent ? PRIMARY_BLUE : isVersionSelected ? PRIMARY_BLUE : "rgba(0,0,0,0.6)",
                                            color: "#fff",
                                            fontSize: 9,
                                            px: 0.75,
                                            py: 0.25,
                                            borderRadius: 1,
                                            zIndex: 1
                                          }}>
                                            v{version.version_number}{version.isCurrent ? " (Current)" : ""}
                                          </Box>
                                          <Box sx={{
                                            position: "absolute",
                                            top: 4,
                                            right: 4,
                                            backgroundColor: "rgba(0,0,0,0.5)",
                                            borderRadius: "50%",
                                            p: 0.25,
                                            display: "flex",
                                            cursor: "pointer",
                                            zIndex: 2,
                                            "&:hover": { backgroundColor: PRIMARY_BLUE }
                                          }}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleImagePreview(version.url, { type: "Extracted", version: version.version_number, totalVersions: totalVers });
                                          }}>
                                            <ZoomInIcon sx={{ color: "#fff", fontSize: 14 }} />
                                          </Box>
                                          <img
                                            src={version.url}
                                            alt={`Version ${version.version_number}`}
                                            style={{ width: "100%", height: "70px", objectFit: "cover", cursor: "pointer" }}
                                          />
                                          {(() => {
                                            const isBeingRegenerated = regeneratingImageIds.has(selectedSection?.sequence_id) && regeneratingSlotIndices[selectedSection?.sequence_id] === image.slot_index && version.isCurrent;
                                            const isBeingApplied = applyingImageIds.has(selectedSection?.sequence_id) && applyingSlotInfo[selectedSection?.sequence_id]?.slotIndex === image.slot_index && applyingSlotInfo[selectedSection?.sequence_id]?.versionNumber === version.version_number;
                                            if (isBeingRegenerated || isBeingApplied) {
                                              return (
                                                <Box sx={{
                                                  position: "absolute", top: 0, left: 0, right: 0, height: "100%",
                                                  backgroundColor: "rgba(0,0,0,0.7)", display: "flex", flexDirection: "column",
                                                  alignItems: "center", justifyContent: "center", color: "#fff", p: 1, textAlign: "center", zIndex: 2
                                                }}>
                                                  <CircularProgress size={20} sx={{ color: "#fff", mb: 0.5 }} />
                                                  <Typography sx={{ fontSize: "9px", lineHeight: 1.1 }}>
                                                    {regenerationMessages[selectedSection?.sequence_id] || (isBeingApplied ? "Applying..." : "Processing...")}
                                                  </Typography>
                                                </Box>
                                              );
                                            }
                                            return null;
                                          })()}
                                          <Box sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            py: 0.5,
                                            backgroundColor: version.isCurrent ? "#e8f0ff" : isVersionSelected ? "#e8f0ff" : "#f5f5f5",
                                          }}>
                                            <Typography variant="caption" sx={{ color: version.isCurrent ? PRIMARY_BLUE : isVersionSelected ? PRIMARY_BLUE : "#666", fontSize: "0.65rem" }}>
                                              {version.isCurrent ? "(Applied)" : isVersionSelected ? "Click Apply" : "Click to Select"}
                                            </Typography>
                                          </Box>
                                        </Box>
                                      );
                                    })}
                                  </React.Fragment>
                                );
                              })}
                            </Box>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  );
                })()}
              </Box>
            </Box>
          </Box>

          {/* Timeline Section */}
          <Box sx={{
            backgroundColor: "#fff",
            borderTop: "1px solid #e0e0e0",
            padding: "8px 24px",
            maxHeight: "130px",
            overflow: "hidden"
          }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Timeline
                </Typography>
                <Tooltip title="Refresh Timeline" arrow>
                  <IconButton
                    size="small"
                    onClick={fetchVideoContent}
                    sx={{ color: "#888", "&:hover": { color: PRIMARY_BLUE } }}
                  >
                    <AutorenewIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Typography variant="body2" sx={{ color: "#666" }}>
                  {selectedSections.size} of {sections.length} sections selected
                </Typography>
                <Button
                  size="small"
                  onClick={handleSelectAll}
                  sx={{ color: PRIMARY_BLUE, textTransform: "none" }}
                >
                  {selectedSections.size === sections.length ? "Deselect All" : "Select All"}
                </Button>
              </Box>
            </Box>

            {/* Timeline Cards with Arrow Navigation */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {/* Left Arrow */}
              <IconButton
                onClick={() => {
                  const container = document.getElementById('timeline-cards-container');
                  if (container) container.scrollBy({ left: -160, behavior: 'smooth' });
                }}
                sx={{
                  backgroundColor: "#f5f5f5",
                  border: "1px solid #e0e0e0",
                  "&:hover": { backgroundColor: "#e0e0e0" },
                  minWidth: 32,
                  height: 32
                }}
              >
                <ChevronLeftIcon sx={{ fontSize: 20 }} />
              </IconButton>

              <Box
                id="timeline-cards-container"
                sx={{
                  display: "flex",
                  gap: 2,
                  overflowX: "auto",
                  flex: 1,
                  pb: 1.5,
                  scrollBehavior: "smooth",
                  /* Custom Scrollbar Styling */
                  "&::-webkit-scrollbar": {
                    height: "6px",
                  },
                  "&::-webkit-scrollbar-track": {
                    backgroundColor: "#f1f1f1",
                    borderRadius: "10px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "#ccc",
                    borderRadius: "10px",
                    "&:hover": {
                      backgroundColor: PRIMARY_BLUE,
                    },
                  },
                }}
              >
                {isLoading ? (
                  <Box sx={{ display: "flex", justifyContent: "center", width: "100%", py: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : sections.map((section, index) => (
                  <Box
                    key={section.sequence_id}
                    onClick={() => {
                      // Pause video and reset playback state when switching sections
                      if (videoRef.current) {
                        videoRef.current.pause();
                      }
                      setIsPlaying(false);
                      setCurrentTime(0);

                      setSelectedSectionIndex(index);
                      // Reset to Content tab if new section has no media
                      const newSection = sections[index];
                      const hasMedia = newSection && (getAiImages(newSection).length > 0 || getExtractedImages(newSection).length > 0);
                      if (!hasMedia && activeTab === 1) {
                        setActiveTab(0);
                      }
                    }}
                    sx={{
                      minWidth: 140,
                      maxWidth: 140,
                      padding: "8px",
                      borderRadius: "8px",
                      border: selectedSectionIndex === index ? `2px solid ${PRIMARY_BLUE}` : "1px solid #e0e0e0",
                      backgroundColor: selectedSectionIndex === index ? "#f0f4ff" : "#fff",
                      cursor: "pointer",
                      flexShrink: 0,
                      transition: "all 0.2s",
                      display: "flex",
                      flexDirection: "column",
                      "&:hover": { borderColor: PRIMARY_BLUE }
                    }}
                  >
                    {/* Header with checkbox */}
                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 0.25 }}>
                      <IconButton
                        size="small"
                        onClick={(e) => toggleSectionSelection(index, e)}
                        sx={{ p: 0, mt: 0.25 }}
                      >
                        {selectedSections.has(index) ? (
                          <CheckCircleIcon sx={{ color: PRIMARY_BLUE, fontSize: 18 }} />
                        ) : (
                          <CircleIcon sx={{ color: "#ccc", fontSize: 18 }} />
                        )}
                      </IconButton>

                    </Box>

                    {/* Template Name */}
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#333",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        lineHeight: 1.3,
                        fontWeight: 400,
                        fontSize: "0.7rem"
                      }}
                    >
                      {section.template_name || `Section ${index + 1}`}
                    </Typography>

                    {/* Footer icons - fixed at bottom */}
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: "auto", pt: 0.5 }}>
                      <Box sx={{ display: "flex", gap: 0.5, alignItems: "center", height: 14 }}>
                        <MicIcon sx={{ fontSize: 12, color: "#888" }} />
                        {(section.images?.slots?.length > 0 || section.images?.versions?.length > 0) && (
                          <ImageIcon sx={{ fontSize: 12, color: "#888" }} />
                        )}
                      </Box>
                      <IconButton size="small" sx={{ p: 0 }}>
                        <Box component="span" sx={{ fontSize: 12, color: "#888" }}>⋯</Box>
                      </IconButton>
                    </Box>
                  </Box>
                ))}

                {/* Add Section Card */}
                <Tooltip title="Coming Soon" arrow placement="top">
                  <Box
                    sx={{
                      minWidth: 100,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: "12px",
                      borderRadius: "8px",
                      border: "2px dashed #ccc",
                      cursor: "not-allowed",
                      opacity: 0.7,
                      flexShrink: 0,
                      "&:hover": { borderColor: "#999" }
                    }}
                  >
                    <Box sx={{ fontSize: 24, color: "#999" }}>+</Box>
                    <Typography variant="caption" sx={{ color: "#999" }}>Add Section</Typography>
                  </Box>
                </Tooltip>
              </Box>

              {/* Right Arrow */}
              <IconButton
                onClick={() => {
                  const container = document.getElementById('timeline-cards-container');
                  if (container) container.scrollBy({ left: 160, behavior: 'smooth' });
                }}
                sx={{
                  backgroundColor: "#f5f5f5",
                  border: "1px solid #e0e0e0",
                  "&:hover": { backgroundColor: "#e0e0e0" },
                  minWidth: 32,
                  height: 32
                }}
              >
                <ChevronRightIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Box>
          </Box>

          {/* Image Preview Modal */}
          {previewImage && (
            <Box
              onClick={handleClosePreview}
              sx={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(0, 0, 0, 0.85)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 1400,
                cursor: "pointer",
              }}
            >
              <Box
                onClick={(e) => e.stopPropagation()}
                sx={{
                  position: "relative",
                  maxWidth: "80vw",
                  maxHeight: "80vh",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                {/* Close button */}
                <IconButton
                  onClick={handleClosePreview}
                  sx={{
                    position: "absolute",
                    top: -40,
                    right: -40,
                    color: "#fff",
                    backgroundColor: "rgba(255,255,255,0.1)",
                    "&:hover": { backgroundColor: "rgba(255,255,255,0.2)" },
                  }}
                >
                  <CloseIcon />
                </IconButton>

                {/* Image */}
                <img
                  src={previewImage.url}
                  alt="Preview"
                  style={{
                    maxWidth: "100%",
                    maxHeight: "70vh",
                    objectFit: "contain",
                    borderRadius: "8px",
                    boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
                  }}
                />

                {/* Image info */}
                {previewImage.info && (
                  <Box sx={{ mt: 2, textAlign: "center" }}>
                    <Typography variant="body2" sx={{ color: "#fff" }}>
                      {previewImage.info.type} Image
                      {previewImage.info.version && ` • Version ${previewImage.info.version}`}
                      {previewImage.info.totalVersions > 1 && ` of ${previewImage.info.totalVersions}`}
                    </Typography>
                  </Box>
                )}

                {/* Click outside hint */}
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.5)", mt: 2 }}>
                  Click anywhere outside to close
                </Typography>
              </Box>
            </Box>
          )}

          {/* Merged Video Dialog using AbstractVideoViewDialog */}
          <AbstractVideoViewDialog
            open={showMergedVideoDialog}
            onClose={handleCloseMergedVideoDialog}
            videoProjectId={videoProjectId}
            onDownload={() => { }}
            onEdit={() => {
              setShowMergedVideoDialog(false);
              setActiveTab(0); // Switch to Content tab
              fetchVideoContent(); // Refresh video content data
            }}
          />

          {/* Snackbar for alerts */}
          <Snackbar
            open={alertOpen}
            autoHideDuration={4000}
            onClose={handleAlertClose}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <Alert onClose={handleAlertClose} severity={alertSeverity} sx={{ width: "100%" }}>
              {alertMessage}
            </Alert>
          </Snackbar>

          {/* Confirmation Dialog */}
          <Dialog
            open={confirmDialogOpen}
            onClose={handleConfirmDialogClose}
            PaperProps={{
              sx: {
                borderRadius: 2,
                minWidth: 350,
                p: 1
              }
            }}
          >
            <DialogTitle sx={{ fontWeight: 600, fontSize: "1.1rem", pb: 1 }}>
              Confirm Apply
            </DialogTitle>
            <DialogContent>
              <DialogContentText sx={{ color: "#555" }}>
                Are you sure you want to apply this image? This will regenerate the video with the new image.
              </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button
                onClick={handleConfirmDialogClose}
                sx={{
                  textTransform: "none",
                  color: "#666"
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmDialogConfirm}
                variant="contained"
                sx={{
                  textTransform: "none",
                  backgroundColor: PRIMARY_BLUE,
                  "&:hover": { backgroundColor: "#163aab" }
                }}
              >
                Apply
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </SidebarTopbar>
    </Box>
  );
};

VideoEditorView.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  videoProjectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  token: PropTypes.string,
  publicationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onVideoCreated: PropTypes.func,
};

VideoEditorView.defaultProps = {
  videoProjectId: null,
  token: null,
  publicationId: null,
  onVideoCreated: null,
};

export default VideoEditorView;
